<template>
  <div class="form-view">
    <ParallaxBackground />
    <SplashSection
      id="splash"
      class="page-section"
      :model-url="splashModelUrl"
      :disabled="isNavigationBusy"
      @get-started="handleGetStarted"
      @show-how-it-works="handleShowHowItWorksFromSplash"
      @show-case-studies="handleShowCaseStudies"
    />

    <!-- Campaign Selection Section (Boundary + Experience) -->
    <FeatureSelectionSection
      id="feature-selection"
      class="page-section"
      :features="boundaryOptions"
      :selected-feature-id="template.boundary"
      :experiences="experienceOptions"
      :selected-experience-id="template.experience"
      @select="handleBoundarySelect"
      @select-experience="handleExperienceSelect"
    />

    <!-- Location Configuration Section -->
    <LocationSection
      v-if="template.boundary === 'geo'"
      id="location-config"
      class="page-section"
    />

    <!-- Look & Feel Section -->
    <LookFeelSection
      id="look-feel"
      class="page-section"
      :template-config="templateConfig"
      :is-asset-browser-visible="isAssetModalVisible"
      :is-upload-browser-visible="isUploadModalVisible"
      :brand-colors="registrationBrandColors"
      :interaction-brand-colors="interactionBrandColors"
      :prize-brand-colors="prizeBrandColors"
      :google-font="lookFeel.googleFont"
      :logo-preview-url="logoPreviewUrl"
      :background-preview-url="registrationBackgroundPreviewUrl"
      :prize-background-preview-url="prizeBackgroundPreviewUrl"
      :button-config="registrationButtonConfig"
      :preview-button-style="registrationButtonStyle"
      :interaction-button-config="interactionButtonConfig"
      :interaction-preview-button-style="interactionButtonStyle"
      :prize-button-config="prizeButtonConfig"
      :prize-preview-button-style="prizeButtonStyle"
      :default-logo="defaultLogo"
      @open-upload-modal="handleOpenUploadModal"
      @open-asset-modal="handleOpenAssetModal"
      @clear-asset="clearSelectedAsset($event.type, $event.screen)"
      @update-brand-color="handleBrandColorChange"
      @update-google-font="handleGoogleFontChange"
      @update-button-config="handleScreenButtonConfigUpdate($event.screen, $event.value)"
    />

    <!-- Quiz Configuration Section -->
    <section
      v-if="template.experience === 'quiz' || template.experience === 'survey'"
      id="quiz-config"
      class="page-section experience-attach-section"
    >
      <div class="experience-attach-shell">
        <div class="section-header">
          <h3>Experience Source</h3>
          <p>Use a saved experience from the library or create a quick campaign-specific one.</p>
        </div>

        <!-- Quick create / Use existing pills removed (A1): creator is already in quick-create flow.
             Library mode is still entered programmatically when an existing campaign with a
             saved-experience attachment is loaded. -->

        <div v-if="experienceAttachMode === 'library'" class="experience-attach-library">
          <div v-if="availableExperienceOptions.length === 0" class="experience-attach-empty">
            No published {{ template.experience }} experiences yet. Publish one from the experience library first.
          </div>
          <template v-else>
            <div v-if="selectedAttachedExperience" class="experience-attach-current">
              <div class="experience-attach-current__eyebrow">Attached</div>
              <div class="experience-attach-current__meta">
                <div class="experience-attach-current__identity">
                  <span class="experience-attach-current__title">{{ selectedAttachedExperience.name }}</span>
                  <span class="experience-attach-current__key">{{ selectedAttachedExperience.localKey }}</span>
                </div>
                <div class="experience-attach-current__badges">
                  <span class="experience-attach-current__status" :class="`experience-attach-current__status--${selectedAttachedExperience.status}`">
                    {{ selectedAttachedExperience.status }}
                  </span>
                  <span class="experience-attach-current__version">
                    v{{ selectedAttachedExperience.versionNumber || 1 }}
                  </span>
                </div>
              </div>
              <div class="experience-attach-summary">
                <span>{{ selectedAttachedExperience.templateFamilyId || 'General template' }}</span>
                <span>•</span>
                <span>{{ selectedAttachedExperience.rounds }} rounds</span>
                <span>•</span>
                <span>{{ selectedAttachedExperience.stages }} stages</span>
                <span>•</span>
                <span>{{ selectedAttachedExperience.questions }} questions</span>
              </div>
              <div
                v-if="selectedAttachedLatestPublishedExperience && selectedAttachedLatestPublishedExperience.localKey !== selectedAttachedExperience.localKey"
                class="experience-attach-upgrade"
              >
                <span>
                  Newer published version available: v{{ selectedAttachedLatestPublishedExperience.versionNumber || 1 }}
                </span>
                <button
                  type="button"
                  class="experience-attach-inline-button"
                  @click="selectAttachedExperience(selectedAttachedLatestPublishedExperience.localKey)"
                >
                  Use latest published
                </button>
              </div>
              <div class="experience-attach-current__actions">
                <router-link
                  class="experience-attach-inline-link"
                  :to="{ path: `/dashboard/experiences/${selectedAttachedExperience.localKey}` }"
                >
                  Open in editor
                </router-link>
                <button
                  type="button"
                  class="experience-attach-inline-button"
                  @click="openSavedExperiencesModal"
                >
                  Change
                </button>
                <button
                  type="button"
                  class="experience-attach-inline-button"
                  @click="clearAttachedExperience"
                >
                  Detach
                </button>
              </div>
            </div>

            <div v-if="!selectedAttachedExperience" class="experience-attach-browse">
              <button
                type="button"
                class="experience-attach-inline-button"
                @click="openSavedExperiencesModal"
              >
                Browse saved experiences
              </button>
            </div>

          </template>
        </div>

        <SavedExperiencesModal
          :show="showSavedExperiencesModal"
          :experience-type="(template.experience as 'quiz' | 'survey')"
          :published-options="publishedExperienceOptions"
          :selected-draft-key="selectedExperienceDraftKey"
          :initial-visible-count="initialVisibleExperienceCount"
          @close="closeSavedExperiencesModal"
          @select="handleSavedExperienceSelect"
        />

        <QuizSection
          v-if="experienceAttachMode === 'quick'"
          class="page-section"
          :model-value="quizConfig"
          :campaign-schedule="campaignSchedule"
          :experience-type="template.experience"
          :show-template-picker="true"
          :show-library-picker="true"
          @update:model-value="handleQuizConfigUpdate"
          @open-library="openSavedExperiencesModal"
        />
      </div>
    </section>

    <ArPreviewSection
      v-if="template.experience === 'ar'"
      id="ar-preview"
      class="page-section"
      :template-config="templateConfig"
      :ar-asset-preview-info="arAssetPreviewInfo"
      :logo-preview-url="logoPreviewUrl"
      :background-preview-url="registrationBackgroundPreviewUrl"
      :brand-colors="interactionBrandColors"
      :button-config="interactionButtonConfig"
      :preview-button-style="interactionButtonStyle"
      :ar-tracking-type="template.arTrackingType"
      @open-upload-modal="handleOpenUploadModal"
      @open-asset-modal="handleOpenAssetModal"
      @clear-asset="clearSelectedAsset"
      @update-button-config="handleScreenButtonConfigUpdate('interaction', $event)"
      @set-ar-tracking-type="campaignBuilderStore.setArTrackingType($event)"
    />

    <MapPreviewSection
      v-if="isMapMarkerEnabled"
      id="map-preview"
      class="page-section"
      :template-config="templateConfig"
      :map-marker-preview-url="mapMarkerPreviewUrl"
      :logo-preview-url="logoPreviewUrl"
      :background-preview-url="registrationBackgroundPreviewUrl"
      :brand-colors="interactionBrandColors"
      :button-config="interactionButtonConfig"
      :preview-button-style="interactionButtonStyle"
      @open-upload-modal="handleOpenUploadModal"
      @open-asset-modal="handleOpenAssetModal"
      @clear-asset="clearSelectedAsset"
      @update-button-config="handleScreenButtonConfigUpdate('interaction', $event)"
    />

    <VouchersSection
      v-if="template.experience"
      id="voucher-config"
      class="page-section"
      :voucher-config="template.voucherConfig || null"
      :cta-config="template.ctaButton || null"
      :message="message"
      :logo-preview-url="logoPreviewUrl"
      :background-preview-url="prizeBackgroundPreviewUrl"
      :default-logo="defaultLogo"
      :brand-colors="prizeBrandColors"
      :button-config="prizeButtonConfig"
      :preview-button-style="prizeButtonStyle"
      @update-voucher-config="handleVoucherConfigUpdate"
      @update-cta-config="handleCtaConfigUpdate"
      @update-message="handleMessageUpdate"
      @open-logo-customizer="handleOpenLogoCustomizer"
      @update-button-config="handleScreenButtonConfigUpdate('prize', $event)"
    />
    <!-- Campaign Schedule Section -->
    <ScheduleSection
      id="schedule"
      class="page-section"
      :schedule-validation="scheduleValidation"
      :campaign-schedule="campaignSchedule"
      :scheduled-start="formatScheduledTime()"
      :scheduled-end="formatScheduledEndTime()"
      :time-until-launch="getTimeUntilLaunch()"
      :timezone="getCurrentTimezone()"
      @schedule-change="handleScheduleChange"
    />

    <!-- Preview Experience Section -->
    <PreviewSection 
      id="preview-experience"
      class="page-section"
    />

    <!-- User Details Section -->
    <UserDetailsSection
      id="user-details"
      class="page-section"
      :user-details="userDetails"
      :campaign-name="template.campaignName ?? ''"
      :existing-campaign-names="existingCampaignNames"
      :submit-success="submitSuccess"
      :is-submitting="isSubmitting"
      :is-form-complete="isFormComplete"
      :is-authenticated="authStore.isAuthenticated"
      @update-user-details="updateUserDetails"
      @update-campaign-name="(name: string) => { template.campaignName = name }"
      @submit="submitCampaign"
      @clear-state="clearState"
      @scroll-registration-to-bottom="scrollUserDetailsToBottom"
    />

    <HoldingModal
      v-if="showHoldingModal"
      @close="showHoldingModal = false"
    />

    <GLBInfoModal
      v-if="showGlbInfoModal"
      @close="showGlbInfoModal = false"
    />

    <LaunchModal
      v-if="showLaunchModal && lastSubmissionId"
      :submission-id="lastSubmissionId"
      @close="showLaunchModal = false"
      @complete="handleLaunchComplete"
    />

    <AuthVerificationModal
      v-if="showVerificationModal"
      :email="pendingVerification.email || authStore.userDetails.email"
      :username="pendingVerification.username"
      :code="pendingVerification.code"
      :is-submitting="pendingVerification.isSubmitting"
      :is-resending="pendingVerification.isResending"
      :error="pendingVerification.error"
      @update:code="pendingVerification.code = $event"
      @close="handleVerificationClose"
      @resend="handleVerificationResend"
      @confirm="handleVerificationConfirm"
    />

    <RekognitionModal
      v-if="showRekognitionModal"
      :result="rekognitionResult"
      @close="handleRekognitionAcknowledge"
    />

    <ValidationModal
      v-if="uiStore.errorModal.show"
      :title="uiStore.errorModal.title"
      :message="uiStore.errorModal.message"
      :description="uiStore.errorModal.description"
      :errors="uiStore.errorModal.errors"
      :icon="uiStore.errorModal.icon"
      :button-text="uiStore.errorModal.buttonText"
      @close="uiStore.closeError()"
    />

    <AssetModal
      :show="isAssetModalVisible"
      :default-logos="defaultLogos"
      :default-map-markers="defaultMapMarkers"
      :default-backgrounds="defaultBackgrounds"
      :default-ar-assets="defaultArAssets"
      :selected-logo-id="lookFeel.selectedDefaultAssets.logo || undefined"
      :selected-marker-id="lookFeel.selectedDefaultAssets.mapMarker || undefined"
      :selected-background-id="selectedBackgroundAssetId"
      :selected-ar-asset-id="lookFeel.selectedDefaultAssets.ar || undefined"
      :allowed-tabs="assetModalAllowedTabs"
      :initial-tab="assetModalInitialTab"
      @close="isAssetModalVisible = false"
      @select-asset="handleDefaultAssetSelect"
    />

    <UploadModal
      :show="isUploadModalVisible"
      :allowed-types="uploadModalAllowedTypes"
      @close="isUploadModalVisible = false"
      @upload-complete="handleUploadComplete"
      @remove="handleFileRemove"
    />


    <!-- Global Navigation -->
    <div class="global-navigation">
      <div class="global-navigation__container">
        <button
          type="button"
          class="help-button"
          :style="{ visibility: isOnSplashTarget ? 'hidden' : 'visible' }"
          :disabled="isNavigationBusy"
          aria-label="Open help for this section"
          @click="handleShowHowItWorksFromFooter"
        >
          <span class="button-text" aria-hidden="true">HELP</span>
        </button>

        <div class="global-navigation__actions">
          <button
            type="button"
            class="back-button"
            :style="{ visibility: isOnSplashTarget ? 'hidden' : 'visible' }"
            :disabled="isBackButtonDisabled"
            aria-label="Go to previous section"
            @click="navigateToPrevSection"
          >
            <span class="button-text" aria-hidden="true">Back</span>
          </button>

          <button
            type="button"
            class="continue-button"
            :style="{ visibility: isOnSplashTarget ? 'hidden' : 'visible' }"
            :class="{
              'continue-button--final': isLastSection
            }"
            :disabled="isContinueButtonDisabled"
            :aria-disabled="isContinueButtonDisabled"
            :aria-label="isLastSection ? 'Complete setup' : 'Go to next section'"
            @click="handleContinueClick"
          >
            <span
              v-if="isLastSection"
              class="button-text button-text--final"
            >
              Complete
            </span>
            <span v-else class="button-text" aria-hidden="true">Next</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
/* Global scroll behavior */
html {
  scroll-behavior: smooth;
}

.page-section {
  min-height: var(--section-available-height);
  max-height: none;
  width: 100%;
}

.form-view .page-section:not(#splash) .section-header {
  margin-top: 14px !important;
}

@media screen and (max-width: 380px) {
  .page-section {
    max-height: none;
  }
}
</style>

<script lang="ts">
import { defineComponent, ref, reactive, watch, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia';
import type {
  CampaignConfig,
  BackgroundScreenKey,
  CompletedCampaignSummary,
  CtaButtonConfig,
  FormSubmission,
  GeneratedVoucherConfig,
  ScreenAppearanceConfigs,
  ScreenAppearanceKey,
  ScreenBrandColors,
  ScreenColorKey,
  StagedAssets,
  StyledButtonConfig,
  VoucherConfig,
  QuizConfig,
} from '@/types/form.types'
import { resolveMaxClaimsPerUser } from '@/utils/dropCap'
import type { CampaignSchedule } from '@/components/CampaignScheduler.vue'
import HoldingModal from '@/components/HoldingModal.vue'
import GLBInfoModal from '@/components/GLBInfoModal.vue'
import LaunchModal from '@/components/LaunchModal.vue'
import AuthVerificationModal from '@/components/AuthVerificationModal.vue'
import RekognitionModal from '@/components/RekognitionModal.vue'
import ValidationModal from '@/components/ValidationModal.vue'
import SavedExperiencesModal from '@/components/SavedExperiencesModal.vue'
import { useAuthStore } from '@/store/auth.store'
import { useUIStore } from '@/store/ui.store'
import { useCampaignBuilderStore } from '@/store/campaignBuilder.store'
import { useFormNavigationStore } from '@/store/formNavigation.store'
import {
  buildPreferredUsername
} from '@/utils/StringUtils'
import { ApiService, type UploadResult, type ApiUploadError } from '@/services/api.service'
import { campaignsService } from '@/services/campaigns'
import { saveCompletedCampaign } from '@/utils/completedCampaigns'
import { compileMindBlob, prepareMarkerUpload } from '@/utils/markerProcessing'
import { waitForProvisioningReady } from '@/composables/useProvisioning'
import { BOUNDARY_OPTIONS, EXPERIENCE_OPTIONS, FEATURE_OPTIONS, IR_DEFAULT_CONFIG, buildCampaignBusinessTemplate, getTemplate, type CampaignBoundary, type CampaignExperience, type CampaignFeature, type CampaignService } from '@/config/campaignConfigs'
import { defaultAssets, type DefaultAsset } from '@/config/defaultAssets';
import { buildApprovedAssetUrls } from '@/config/assetBuckets';
import AssetModal from '@/components/AssetModal.vue';
import UploadModal from '@/components/UploadModal.vue';
/* TODO: swap to new IRL logo asset when available */
import defaultLogo from '@/assets/irl-logo.png';
import SplashSection from '@/views/form/sections/SplashSection.vue';
import ParallaxBackground from '@/components/ParallaxBackground.vue';
import FeatureSelectionSection from '@/views/form/sections/FeatureSelectionSection.vue';
import ArPreviewSection from '@/views/form/sections/ArPreviewSection.vue';
import MapPreviewSection from '@/views/form/sections/MapPreviewSection.vue';
import QuizSection from '@/views/form/sections/QuizSection.vue';
import LookFeelSection from '@/views/form/sections/LookFeelSection.vue';
import ScheduleSection from '@/views/form/sections/ScheduleSection.vue';
import PreviewSection from '@/views/form/sections/PreviewSection.vue';
import UserDetailsSection from '@/views/form/sections/UserDetailsSection.vue';
import LocationSection from '@/views/form/sections/LocationSection.vue';
import VouchersSection from '@/views/form/sections/VouchersSection.vue';
import {
  buildButtonPreviewStyle,
  buildButtonStyleFallbacks,
  DEFAULT_BUTTON_CONFIGS,
  ensureHttpsButtonUrl,
  stripButtonUrlProtocol,
  normalizeStyledButtonConfig,
  resolveButtonStyleColors,
  syncButtonColorOverridesWithFallbackChange,
  stripSharedButtonColorOverrides,
} from '@/utils/buttonStyles';
import { buildBuilderDraftState } from '@/utils/campaignEditDraft'
import { experiencesService } from '@/services/experiences'
import { getExperienceLibraryRecord, listExperienceLibraryRecords, materializeExperienceIdentity, saveExperienceLibraryRecord, type ExperienceLibraryRecord } from '@/utils/experienceLibrary'
import { normalizeLocationConfig } from '@/utils/locationConfig'

interface RekognitionErrorInfo {
  errorType: string;
  userMessage: string;
  shouldRetry: boolean;
  timestamp: string;
}

interface RekognitionResult {
  isValid: boolean;
  confidence: number;
  moderationLabels: string[];
  labels: string[];
  assetType: string;
  errorInfo?: RekognitionErrorInfo;
}

type ArPreviewInfo =
  | { type: 'default'; thumbnail?: string | null; name?: string | null }
  | { type: 'staged'; name?: string | null }

type AssetType = 'logo' | 'mapMarker' | 'ar' | 'background'
type BackgroundAssetKey = 'registrationBackground' | 'prizeBackground'

const SCREEN_KEYS: ScreenAppearanceKey[] = ['registration', 'interaction', 'prize']
const BACKGROUND_SCREEN_KEYS: BackgroundScreenKey[] = ['registration', 'prize']
const BACKGROUND_ASSET_KEYS: Record<BackgroundScreenKey, BackgroundAssetKey> = {
  registration: 'registrationBackground',
  prize: 'prizeBackground',
}

// User-facing names for each form section. Used in the submission-blocked
// error message so "voucher-config: Voucher Setup" reads as "Prize Config:
// Voucher Setup (Generate or Upload CSV)" instead of a slug.
const SECTION_LABELS: Record<string, string> = {
  'feature-selection': 'Campaign Type',
  'location-config': 'Location',
  'ar-preview': 'AR Experience',
  'quiz-config': 'Quiz Questions',
  'map-preview': 'Map Marker',
  'voucher-config': 'Prize Configuration',
  'look-feel': 'Look & Feel',
  'schedule': 'Schedule',
  'user-details': 'Your Details',
}

export default defineComponent({
  name: 'FormView',
  emits: ['show-how-it-works', 'show-case-studies'],
  components: {
    HoldingModal,
    GLBInfoModal,
    LaunchModal,
    AuthVerificationModal,
    RekognitionModal,
    ValidationModal,
    SavedExperiencesModal,
    AssetModal,
    UploadModal,
    SplashSection,
    ParallaxBackground,
    FeatureSelectionSection,
    ArPreviewSection,
    MapPreviewSection,
    ScheduleSection,
    LookFeelSection,
    PreviewSection,
    UserDetailsSection,
    LocationSection,
    VouchersSection,
    QuizSection,
  },
  setup(_, { emit }) {
    // Stores
    const uiStore = useUIStore();
    const campaignBuilderStore = useCampaignBuilderStore();
    const formNavigationStore = useFormNavigationStore();
    const { 
      template,
      lookFeel,
      drops,
      message,
      schedule,
      userDetails,
      validation,
      completion,
    } = storeToRefs(campaignBuilderStore);
    const {
      sections,
      activeSectionId,
      visibleSectionId,
      pendingTargetSectionId,
      currentSectionIndex,
      isTransitioning,
      isFirstSection,
      isLastSection,
    } = storeToRefs(formNavigationStore)

    const router = useRouter();
    const route = useRoute();

    // Auth store for user tier
    const authStore = useAuthStore();

    // Splash page 3D model URL from environment
    const splashModelUrl = ref('https://splash-assets.dev.viviewer.com/splash-cube.glb')

    // --- End Pre-populate Dates ---

    // Modal state
    const showHoldingModal = ref(false)
    const showGlbInfoModal = ref(false)
    const showLaunchModal = ref(false)
    const showRekognitionModal = ref(false)
    const showVerificationModal = ref(false)
    const pendingVerification = reactive({
      username: '',
      password: '',
      email: '',
      code: '',
      error: '',
      isSubmitting: false,
      isResending: false,
      resolver: null as null | ((verified: boolean) => void),
    })
    const rekognitionResult = ref<RekognitionResult | null>(null)
    const rekognitionAcknowledgeResolver = ref<(() => void) | null>(null)

    const isAssetModalVisible = ref(false);
    const isUploadModalVisible = ref(false);
    const activeBackgroundScreen = ref<BackgroundScreenKey>('registration');
    const assetModalAllowedTabs = ref<Array<'logos' | 'markers' | 'backgrounds' | 'models'>>(['logos', 'markers', 'backgrounds', 'models'])
    const assetModalInitialTab = ref<'logos' | 'markers' | 'backgrounds' | 'models'>('logos')
    const uploadModalAllowedTypes = ref<AssetType[]>(['logo', 'mapMarker', 'ar', 'background'])
    const editingCampaignId = computed(() => typeof route.params.campaignId === 'string' ? route.params.campaignId : null)
    const isEditMode = computed(() => Boolean(editingCampaignId.value))
    const getBuilderStorageKey = () => editingCampaignId.value ? `campaignBuilderState:${editingCampaignId.value}` : 'campaignBuilderState'

    // Placeholder assets
    const placeholderAssets = {
      logos: [
        { id: 'logo1', name: 'Default Logo 1', s3Url: 'path/to/logo1.png', thumbnailUrl: 'https://via.placeholder.com/100' },
        { id: 'logo2', name: 'Default Logo 2', s3Url: 'path/to/logo2.png', thumbnailUrl: 'https://via.placeholder.com/100' },
      ],
      mapMarkers: [
        { id: 'marker1', name: 'Default Marker 1', s3Url: 'path/to/marker1.png', thumbnailUrl: 'https://via.placeholder.com/100' },
        { id: 'marker2', name: 'Default Marker 2', s3Url: 'path/to/marker2.png', thumbnailUrl: 'https://via.placeholder.com/100' },
      ],
      arAssets: [
        { id: 'ar1', name: 'Default AR 1', s3Url: 'path/to/ar1.glb', thumbnailUrl: 'https://via.placeholder.com/100' },
        { id: 'ar2', name: 'Default AR 2', s3Url: 'path/to/ar2.glb', thumbnailUrl: 'https://via.placeholder.com/100' },
      ],
    }

    watch(showHoldingModal, (isOpen) => {
      if (isOpen) {
        uiStore.openModal()
      } else {
        uiStore.closeModal()
      }
    })

    watch(showGlbInfoModal, (isOpen) => {
      if (isOpen) {
        uiStore.openModal()
      } else {
        uiStore.closeModal()
      }
    })

    watch(showLaunchModal, (isOpen) => {
      if (isOpen) {
        uiStore.openModal()
      } else {
        uiStore.closeModal()
      }
    })

    watch(showRekognitionModal, (isOpen) => {
      if (isOpen) {
        uiStore.openModal()
      } else {
        uiStore.closeModal()
      }
    })

    watch(showVerificationModal, (isOpen) => {
      if (isOpen) {
        uiStore.openModal()
      } else {
        uiStore.closeModal()
      }
    })

    watch(isAssetModalVisible, (isOpen) => {
      if (isOpen) {
        uiStore.openModal()
      } else {
        uiStore.closeModal()
        assetModalAllowedTabs.value = ['logos', 'markers', 'backgrounds', 'models']
        assetModalInitialTab.value = 'logos'
      }
    })

    watch(isUploadModalVisible, (isOpen) => {
      if (isOpen) {
        uiStore.openModal()
      } else {
        uiStore.closeModal()
        uploadModalAllowedTypes.value = ['logo', 'mapMarker', 'ar', 'background']
      }
    })

    const isScrollAllowedTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) {
        return false
      }
      return SCROLL_ALLOWED_SELECTORS.some((selector) => Boolean(target.closest(selector)))
    }

    const isInteractiveTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) {
        return false
      }
      return Boolean(target.closest(INTERACTIVE_ELEMENT_SELECTOR))
    }

    // Computed properties for convenience
    const defaultFaviconAsset = buildApprovedAssetUrls('logo/irl-logo.png')

    const templateConfig = computed(() => template.value)

    const DEFAULT_QUIZ_CONFIG: QuizConfig = {
      experienceMode: 'quiz',
      quizTemplateId: undefined,
      experienceTemplateFamilyId: undefined,
      experienceTemplateVersionId: undefined,
      experienceDefinitionId: undefined,
      experienceDefinitionVersionId: undefined,
      experienceDefinitionName: undefined,
      experienceDefinitionStatus: 'draft',
      experienceDefinitionLocalKey: undefined,
      experienceDefinitionLastSavedAt: undefined,
      timeLimitSeconds: 60,
      passThreshold: 3,
      attemptLimit: 3,
      attemptWindow: 'day',
      rounds: [{
        id: 'round-1',
        label: 'Round 1',
        stages: [{ id: 'round-1-stage-1', order: 1, type: 'quiz', questions: [] }],
      }],
      slots: [{
        id: 'slot-round-1',
        label: 'Round 1',
        roundId: 'round-1',
        releaseType: 'immediate' as const,
      }],
      noActiveSlotAction: null,
      leaderboard: {
        enabled: false,
        eventName: 'quiz.completed.score',
        displayMode: 'top-10-plus-user',
        title: 'Top Players',
        emptyStateText: 'Be the first to post a score.',
        showAfterCompletionOnly: true,
      },
      primaryColor: '#9B6DFF',
      backgroundColor: '#050614',
      correctColor: '#39FF14',
      incorrectColor: '#FF3131',
    }

    const buildDefaultSlotForRound = (round: QuizConfig['rounds'][number], index: number): QuizConfig['slots'][number] => ({
      id: `slot-${round.id || `round-${index + 1}`}`,
      label: round.label || `Round ${index + 1}`,
      roundId: round.id || `round-${index + 1}`,
      releaseType: index === 0 ? 'immediate' : 'offset',
      offsetDays: index === 0 ? undefined : index * 7,
      releaseDate: undefined,
    })

    const reconcileQuizSlots = (
      rounds: QuizConfig['rounds'],
      slots: QuizConfig['slots'] | undefined,
    ): QuizConfig['slots'] => {
      const slotByRoundId = new Map((slots ?? []).map((slot) => [slot.roundId, { ...slot }]))
      return rounds.map((round, index) => {
        const existing = slotByRoundId.get(round.id)
        if (existing) {
          return {
            ...existing,
            id: existing.id || `slot-${round.id}`,
            label: existing.label || round.label || `Round ${index + 1}`,
            roundId: round.id,
          }
        }
        return buildDefaultSlotForRound(round, index)
      })
    }

    const normalizeQuizConfig = (config?: QuizConfig | null): QuizConfig => {
      const source = config ?? DEFAULT_QUIZ_CONFIG
      const legacyRounds = (source.rounds ?? []) as Array<QuizConfig['rounds'][number] & { questions?: any[]; releaseType?: string; offsetDays?: number; releaseDate?: string }>
      const rounds = legacyRounds.map((round, index) => {
        const stages = round.stages?.length
          ? round.stages.map((stage, stageIndex) => ({
              ...stage,
              order: stageIndex + 1,
              type: (stage.type || (source.experienceMode === 'survey' ? 'survey' : 'quiz')) as 'quiz' | 'survey',
              questions: (stage.questions ?? []).map((question) => ({
                ...question,
                choices: (question.choices ?? []).map((choice: any) => ({ ...choice })),
              })),
            }))
          : [{
              id: `${round.id || `round-${index + 1}`}-stage-1`,
              order: 1,
              type: (source.experienceMode === 'survey' ? 'survey' : 'quiz') as 'quiz' | 'survey',
              questions: (round.questions ?? []).map((question) => ({
                ...question,
                choices: (question.choices ?? []).map((choice: any) => ({ ...choice })),
              })),
            }]

        return {
          id: round.id || `round-${index + 1}`,
          label: round.label || `Round ${index + 1}`,
          stages,
        }
      })

      const sourceSlots = source.slots?.length
        ? source.slots.map((slot) => ({ ...slot }))
        : legacyRounds.map((round, index) => ({
            id: `slot-${round.id || `round-${index + 1}`}`,
            label: round.label || `Round ${index + 1}`,
            roundId: round.id || `round-${index + 1}`,
            releaseType: (round.releaseType as any) || (index === 0 ? 'immediate' : 'offset'),
            offsetDays: round.offsetDays,
            releaseDate: round.releaseDate,
            releaseTime: (round as any).releaseTime,
          }))
      const slots = reconcileQuizSlots(rounds, sourceSlots)

      return {
        ...DEFAULT_QUIZ_CONFIG,
        ...source,
        rounds,
        slots: slots.map((slot, index) => ({
          ...slot,
          id: slot.id || `slot-${slot.roundId || `round-${index + 1}`}`,
          label: slot.label || `Slot ${index + 1}`,
        })),
        leaderboard: source.leaderboard
          ? {
              enabled: source.leaderboard.enabled === true,
              eventName: source.leaderboard.eventName || 'quiz.completed.score',
              displayMode: 'top-10-plus-user',
              title: source.leaderboard.title || 'Top Players',
              emptyStateText: source.leaderboard.emptyStateText || 'Be the first to post a score.',
              showAfterCompletionOnly: source.leaderboard.showAfterCompletionOnly !== false,
            }
          : { ...DEFAULT_QUIZ_CONFIG.leaderboard! },
      }
    }

    const quizConfig = computed<QuizConfig>(() => normalizeQuizConfig(template.value.quizConfig))
    const experienceAttachMode = ref<'quick' | 'library'>('quick')
    const hasChosenExperienceAttachMode = ref(false)
    const showSavedExperiencesModal = ref(false)
    const openSavedExperiencesModal = () => {
      showSavedExperiencesModal.value = true
    }
    const closeSavedExperiencesModal = () => {
      showSavedExperiencesModal.value = false
    }
    const setExperienceAttachMode = (mode: 'quick' | 'library') => {
      experienceAttachMode.value = mode
      hasChosenExperienceAttachMode.value = true
      if (mode === 'library' && !selectedExperienceDraftKey.value) {
        showSavedExperiencesModal.value = true
      }
    }
    const selectedExperienceDraftKey = ref('')
    const initialVisibleExperienceCount = 3
    const orgStamp = () => ({
      orgId: authStore.activeOrgId || undefined,
      tenantId: authStore.activeTenantId || undefined,
    })
    const stripIdPrefix = (id: string | null | undefined) =>
      (id || '').replace(/^[A-Z]+#/, '')
    const sameEntity = (recordId: string | undefined, activeId: string | null) => {
      if (!recordId || !activeId) return true
      return stripIdPrefix(recordId) === stripIdPrefix(activeId)
    }
    const availableExperienceOptions = computed(() => {
      const activeOrgId = authStore.activeOrgId
      const activeTenantId = authStore.activeTenantId
      return listExperienceLibraryRecords().filter((record) => {
        const mode = record.experienceMode === 'survey' ? 'survey' : 'quiz'
        if (mode !== template.value.experience || record.status !== 'published') return false
        if (!sameEntity(record.orgId, activeOrgId)) return false
        if (!sameEntity(record.tenantId, activeTenantId)) return false
        return true
      })
    })
    const latestPublishedExperienceByLineage = computed(() => {
      const latest = new Map<string, ExperienceLibraryRecord>()
      for (const record of availableExperienceOptions.value) {
        const lineageKey = record.lineageId || record.quizConfig.experienceDefinitionId || record.localKey
        const existing = latest.get(lineageKey)
        if (!existing) {
          latest.set(lineageKey, record)
          continue
        }
        const currentVersion = record.versionNumber || 1
        const existingVersion = existing.versionNumber || 1
        if (currentVersion > existingVersion) {
          latest.set(lineageKey, record)
          continue
        }
        if (currentVersion === existingVersion && new Date(record.updatedAt).getTime() > new Date(existing.updatedAt).getTime()) {
          latest.set(lineageKey, record)
        }
      }
      return latest
    })
    const publishedExperienceOptions = computed(() =>
      availableExperienceOptions.value.filter((record) => {
        const lineageKey = record.lineageId || record.quizConfig.experienceDefinitionId || record.localKey
        return latestPublishedExperienceByLineage.value.get(lineageKey)?.localKey === record.localKey
      }),
    )
    const selectedAttachedExperience = computed<ExperienceLibraryRecord | null>(() =>
      selectedExperienceDraftKey.value ? getExperienceLibraryRecord(selectedExperienceDraftKey.value) : null,
    )
    const selectedAttachedLatestPublishedExperience = computed<ExperienceLibraryRecord | null>(() => {
      const record = selectedAttachedExperience.value
      if (!record) return null
      const lineageKey = record.lineageId || record.quizConfig.experienceDefinitionId || record.localKey
      return latestPublishedExperienceByLineage.value.get(lineageKey) || null
    })

    const buildAttachedExperienceReference = (
      record?: ExperienceLibraryRecord | null,
    ): CampaignConfig['attachedExperience'] => {
      if (!record || record.status !== 'published') {
        return null
      }

      return {
        source: 'shared-published',
        localKey: record.localKey,
        experienceId: record.localKey,
        lineageId: record.lineageId || record.quizConfig.experienceDefinitionId || record.localKey,
        versionId: record.quizConfig.experienceDefinitionVersionId || (record.versionNumber ? `v${record.versionNumber}` : 'v1'),
        versionNumber: record.versionNumber || 1,
        name: record.name,
        status: 'published',
        templateFamilyId: record.templateFamilyId || record.quizConfig.experienceTemplateFamilyId || record.quizConfig.quizTemplateId,
        experienceMode: record.experienceMode || record.quizConfig.experienceMode || 'quiz',
      }
    }

    const handleQuizConfigUpdate = (config: QuizConfig) => {
      const normalized = materializeExperienceIdentity(normalizeQuizConfig(config))
      campaignBuilderStore.setQuizConfig(normalized)
      saveExperienceLibraryRecord({
        quizConfig: normalized,
        campaignName: template.value.campaignName || undefined,
        ...orgStamp(),
      })
      const allQuestions = normalized.rounds.flatMap(r => r.stages.flatMap(s => s.questions))
      const hasQuestions = allQuestions.length > 0
      const isQuestionValid = (question: typeof allQuestions[number], stageType?: 'quiz' | 'survey') => {
        const responseType = question.type === 'quiz' ? 'single-select' : (question.type || (stageType === 'survey' ? 'single-select' : 'single-select'))
        const promptFormat = question.promptFormat || ((question.prompt?.videoAssetId && 'video') || (question.prompt?.imageAssetId && 'image') || 'text')
        if (!question.text.trim()) return false
        if (promptFormat === 'image' && !question.prompt?.imageAssetId?.trim()) return false
        if (promptFormat === 'video' && !question.prompt?.videoAssetId?.trim()) return false
        if (responseType === 'free-text') return true
        if (responseType === 'rating') return Boolean((question.ratingScale?.min ?? 1) && (question.ratingScale?.max ?? 5))
        if (responseType === 'boolean') return true
        const hasValidChoices = question.choices.length >= 2 && question.choices.every(c => c.label.trim() || c.imageAssetId?.trim())
        if (!hasValidChoices) return false
        if (responseType === 'multi-select') return Boolean(question.correctChoiceIds?.length)
        if (normalized.experienceMode === 'survey' || stageType === 'survey') return true
        return Boolean(question.correctChoiceId)
      }
      const allQuestionsValid = normalized.rounds.every((round) =>
        round.stages.every((stage) => stage.questions.every((question) => isQuestionValid(question, stage.type)))
      )
      if (hasQuestions && allQuestionsValid) {
        uiStore.completeStep('quiz-config')
      } else {
        uiStore.removeStep('quiz-config')
      }
    }

    const handleExperienceDraftSelect = () => {
      if (!selectedExperienceDraftKey.value) {
        return
      }

      const record = getExperienceLibraryRecord(selectedExperienceDraftKey.value)
      if (!record || record.status !== 'published') {
        selectedExperienceDraftKey.value = ''
        experienceAttachMode.value = 'quick'
        template.value.attachedExperience = null
        return
      }

      const experience = (record.quizConfig.experienceMode === 'survey' ? 'survey' : 'quiz') as CampaignExperience
      campaignBuilderStore.setExperience(experience)
      campaignBuilderStore.setExperienceType(experience)
      const hydratedQuizConfig = materializeExperienceIdentity(normalizeQuizConfig(record.quizConfig))
      campaignBuilderStore.setQuizConfig(hydratedQuizConfig)
      saveExperienceLibraryRecord({
        quizConfig: hydratedQuizConfig,
        campaignName: template.value.campaignName || record.name,
        ...orgStamp(),
      })
      if (!template.value.campaignName && record.name) {
        template.value.campaignName = record.name
      }
      template.value.attachedExperience = buildAttachedExperienceReference(record)
    }

    const selectAttachedExperience = (localKey: string) => {
      selectedExperienceDraftKey.value = localKey
      handleExperienceDraftSelect()
      template.value.attachedExperience = buildAttachedExperienceReference(getExperienceLibraryRecord(localKey))
    }

    const clearAttachedExperience = () => {
      selectedExperienceDraftKey.value = ''
      experienceAttachMode.value = 'library'
      template.value.attachedExperience = null
    }

    const handleSavedExperienceSelect = (localKey: string) => {
      selectAttachedExperience(localKey)
      showSavedExperiencesModal.value = false
    }

    const isMapMarkerEnabled = computed(() => {
      return template.value.boundary === 'geo' || template.value.experience === 'map'
    })
    const screenBrandColors = computed(() => lookFeel.value.screenBrandColors)
    const registrationBrandColors = computed(() => screenBrandColors.value.registration)
    const interactionBrandColors = computed(() => screenBrandColors.value.interaction)
    const prizeBrandColors = computed(() => screenBrandColors.value.prize)
    const getContrastColor = (hexColor: string) => {
      if (!hexColor) {
        return '#1a1a1a'
      }

      const sanitized = hexColor.replace('#', '')
      const expanded = sanitized.length === 3
        ? sanitized.split('').map((char) => `${char}${char}`).join('')
        : sanitized.padEnd(6, sanitized[sanitized.length - 1] ?? '0')

      const r = parseInt(expanded.substring(0, 2), 16)
      const g = parseInt(expanded.substring(2, 4), 16)
      const b = parseInt(expanded.substring(4, 6), 16)

      if ([r, g, b].some((value) => Number.isNaN(value))) {
        return '#1a1a1a'
      }

      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
      return luminance > 0.6 ? '#1a1a1a' : '#ffffff'
    }

    const buildButtonFallbacks = (colors: ScreenBrandColors) => {
      const buttonColor = colors.secondary || 'var(--vt-c-accent-dark)'
      return buildButtonStyleFallbacks({
        secondary: buttonColor,
        buttonText: colors.buttonText || getContrastColor(buttonColor),
      })
    }
    const registrationButtonConfig = computed(() =>
      normalizeStyledButtonConfig(
        template.value.buttonConfigs?.registration,
        DEFAULT_BUTTON_CONFIGS.registration,
      ),
    )
    const interactionButtonConfig = computed(() =>
      normalizeStyledButtonConfig(
        template.value.buttonConfigs?.interaction,
        DEFAULT_BUTTON_CONFIGS.interaction,
      ),
    )
    const prizeButtonConfig = computed(() =>
      normalizeStyledButtonConfig(
        template.value.buttonConfigs?.prize,
        DEFAULT_BUTTON_CONFIGS.prize,
      ),
    )
    const registrationButtonStyle = computed(() =>
      buildButtonPreviewStyle(registrationButtonConfig.value, buildButtonFallbacks(registrationBrandColors.value), DEFAULT_BUTTON_CONFIGS.registration),
    )
    const interactionButtonStyle = computed(() =>
      buildButtonPreviewStyle(interactionButtonConfig.value, buildButtonFallbacks(interactionBrandColors.value), DEFAULT_BUTTON_CONFIGS.interaction),
    )
    const prizeButtonStyle = computed(() =>
      buildButtonPreviewStyle(prizeButtonConfig.value, buildButtonFallbacks(prizeBrandColors.value), DEFAULT_BUTTON_CONFIGS.prize),
    )
    const uploadedAssets = computed(() => lookFeel.value.uploadedAssets)
    const dropConfig = computed(() => drops.value)
    const prizeMessage = computed(() => message.value)
    const campaignSchedule = computed(() => schedule.value)
    const scheduleValidation = computed(() => validation.value.schedule)
    const defaultLogos = computed(() => defaultAssets.filter(a => a.type === 'logo'));
    const defaultMapMarkers = computed(() => defaultAssets.filter(a => a.type === 'mapMarker'));
    const defaultBackgrounds = computed(() => defaultAssets.filter(a => a.type === 'background'));
    const defaultArAssets = computed(() => defaultAssets.filter(a => a.type === 'ar'));
    const getBackgroundAssetKey = (screen: BackgroundScreenKey): BackgroundAssetKey => BACKGROUND_ASSET_KEYS[screen]
    const getBackgroundSelectionKey = (screen: BackgroundScreenKey): BackgroundAssetKey => BACKGROUND_ASSET_KEYS[screen]

    // Asset UI Logic
    const clearSelectedAsset = (type: 'logo' | 'mapMarker' | 'ar' | 'background', screen: BackgroundScreenKey = 'registration') => {
      if (type === 'logo') {
        campaignBuilderStore.lookFeel.stagedAssets.logo = undefined;
        campaignBuilderStore.lookFeel.selectedDefaultAssets.logo = null;
        campaignBuilderStore.lookFeel.uploadedAssets.logo = undefined;
      } else if (type === 'mapMarker') {
        campaignBuilderStore.lookFeel.stagedAssets.mapMarker = undefined;
        campaignBuilderStore.lookFeel.selectedDefaultAssets.mapMarker = null;
        campaignBuilderStore.lookFeel.uploadedAssets.mapMarker = undefined;
      } else if (type === 'ar') {
        campaignBuilderStore.lookFeel.stagedAssets.glbModel = undefined;
        campaignBuilderStore.lookFeel.selectedDefaultAssets.ar = null;
        campaignBuilderStore.lookFeel.uploadedAssets.glbModel = undefined;
      } else if (type === 'background') {
        const assetKey = getBackgroundAssetKey(screen);
        const selectionKey = getBackgroundSelectionKey(screen);
        campaignBuilderStore.lookFeel.stagedAssets[assetKey] = undefined;
        campaignBuilderStore.lookFeel.selectedDefaultAssets[selectionKey] = null;
        campaignBuilderStore.lookFeel.uploadedAssets[assetKey] = undefined;
      }
    };

    const syncScreenButtonConfigToSharedColors = (
      screen: ScreenAppearanceKey,
      previousFallbacks = buildButtonFallbacks(screenBrandColors.value[screen]),
      nextFallbacks = buildButtonFallbacks(screenBrandColors.value[screen]),
    ) => {
      if (!template.value.buttonConfigs?.[screen]) {
        return
      }

      template.value.buttonConfigs = {
        registration: normalizeScreenButtonConfig('registration', template.value.buttonConfigs?.registration),
        interaction: normalizeScreenButtonConfig('interaction', template.value.buttonConfigs?.interaction),
        prize: normalizeScreenButtonConfig('prize', template.value.buttonConfigs?.prize),
        ...template.value.buttonConfigs,
        [screen]: normalizeScreenButtonConfig(
          screen,
          syncButtonColorOverridesWithFallbackChange(
            template.value.buttonConfigs?.[screen],
            DEFAULT_BUTTON_CONFIGS[screen],
            previousFallbacks,
            nextFallbacks,
          ),
        ),
      }
    }

    const handleBrandColorChange = (payload: { screen: ScreenAppearanceKey; key: ScreenColorKey; value: string }) => {
      const previousFallbacks = buildButtonFallbacks(screenBrandColors.value[payload.screen])
      campaignBuilderStore.setScreenBrandColor(payload.screen, payload.key, payload.value);
      if (payload.key === 'secondary' || payload.key === 'buttonText') {
        syncScreenButtonConfigToSharedColors(
          payload.screen,
          previousFallbacks,
          buildButtonFallbacks(screenBrandColors.value[payload.screen]),
        )
      }
    };

    const handleGoogleFontChange = (font: string) => {
      campaignBuilderStore.setGoogleFont(font);
    };

    const assetTypeToTabMap: Record<AssetType, 'logos' | 'markers' | 'backgrounds' | 'models'> = {
      logo: 'logos',
      mapMarker: 'markers',
      background: 'backgrounds',
      ar: 'models',
    }

    const handleOpenAssetModal = ({ type, screen }: { type: AssetType; screen?: BackgroundScreenKey }) => {
      if (type === 'mapMarker' && !isMapMarkerEnabled.value) {
        return
      }
      if (type === 'background') {
        activeBackgroundScreen.value = screen ?? 'registration'
      }
      const initialTab = assetTypeToTabMap[type] ?? 'logos'
      assetModalAllowedTabs.value = [initialTab]
      assetModalInitialTab.value = initialTab
      isAssetModalVisible.value = true
    }

    const handleOpenLogoCustomizer = () => {
      handleOpenAssetModal({ type: 'logo' })
    }

    const handleOpenUploadModal = ({ type, screen }: { type: AssetType; screen?: BackgroundScreenKey }) => {
      if (type === 'mapMarker' && !isMapMarkerEnabled.value) {
        return
      }

      if (type === 'background') {
        activeBackgroundScreen.value = screen ?? 'registration'
      }

      uploadModalAllowedTypes.value = [type]
      isUploadModalVisible.value = true
    }

    const updateUserDetails = (details: Partial<typeof userDetails.value>) => {
      campaignBuilderStore.updateUserDetails(details);
    };

    const logoPreviewUrl = computed(() => {
      if (lookFeel.value.stagedAssets.logo?.localPreview) {
        return lookFeel.value.stagedAssets.logo.localPreview;
      }
      if (lookFeel.value.selectedDefaultAssets.logo) {
        const asset = defaultLogos.value.find(a => a.id === lookFeel.value.selectedDefaultAssets.logo);
        return asset?.thumbnailUrl;
      }
      return null;
    });

    const mapMarkerPreviewUrl = computed(() => {
      if (!isMapMarkerEnabled.value) {
        return null;
      }
      if (lookFeel.value.stagedAssets.mapMarker?.localPreview) {
        return lookFeel.value.stagedAssets.mapMarker.localPreview;
      }
      if (lookFeel.value.selectedDefaultAssets.mapMarker) {
        const asset = defaultMapMarkers.value.find(a => a.id === lookFeel.value.selectedDefaultAssets.mapMarker);
        return asset?.thumbnailUrl;
      }
      return null;
    });

    const resolveBackgroundPreviewUrl = (screen: BackgroundScreenKey) => {
      const assetKey = getBackgroundAssetKey(screen)
      const selectionKey = getBackgroundSelectionKey(screen)

      // 1. Mid-upload staged preview (local blob)
      if (lookFeel.value.stagedAssets[assetKey]?.localPreview) {
        return lookFeel.value.stagedAssets[assetKey]?.localPreview ?? null
      }
      // 2. Picker-selected default asset
      if (lookFeel.value.selectedDefaultAssets[selectionKey]) {
        const asset = defaultBackgrounds.value.find(a => a.id === lookFeel.value.selectedDefaultAssets[selectionKey])
        if (asset?.thumbnailUrl) return asset.thumbnailUrl
      }
      // 3. Current uploadedAssets entry — seeded at store init, may reflect a
      //    user upload that replaced the default.
      const uploadedUrl = lookFeel.value.uploadedAssets[assetKey]?.s3Url
      if (uploadedUrl) return uploadedUrl
      // 4. Hard fallback — the system default image that is sent in the
      //    campaign payload when the user makes no choice. Reading straight
      //    from IR_DEFAULT_CONFIG guarantees the preview never renders empty,
      //    even if persisted state or a clear action wiped uploadedAssets.
      return IR_DEFAULT_CONFIG.lookFeel.preBuiltUploadedAssets[assetKey]?.s3Url ?? null
    }

    const registrationBackgroundPreviewUrl = computed(() => resolveBackgroundPreviewUrl('registration'))
    const prizeBackgroundPreviewUrl = computed(() => resolveBackgroundPreviewUrl('prize'))

    // Names of campaigns the user already owns — used by the register modal
    // to generate a unique default campaign name without clashing. Kicked
    // off async on mount when authenticated; errors are swallowed because
    // a missing list just means we fall through to the bare default.
    const existingCampaignNames = ref<string[]>([])
    const refreshExistingCampaignNames = async () => {
      if (!authStore.isAuthenticated) {
        existingCampaignNames.value = []
        return
      }
      try {
        const list = await campaignsService.listCampaigns(200)
        existingCampaignNames.value = list
          .map(c => c.name?.trim())
          .filter((n): n is string => Boolean(n))
      } catch {
        existingCampaignNames.value = []
      }
    }
    const selectedBackgroundAssetId = computed(() => {
      const selectionKey = getBackgroundSelectionKey(activeBackgroundScreen.value)
      return lookFeel.value.selectedDefaultAssets[selectionKey] || undefined
    })

    const arAssetPreviewInfo = computed<ArPreviewInfo | null>(() => {
        if (lookFeel.value.stagedAssets.glbModel?.fileName) {
            return {
                type: 'staged' as const,
                name: lookFeel.value.stagedAssets.glbModel.fileName
            };
        }
        if (lookFeel.value.selectedDefaultAssets.ar) {
            const asset = defaultArAssets.value.find(a => a.id === lookFeel.value.selectedDefaultAssets.ar);
            if (asset) {
                return {
                    type: 'default' as const,
                    thumbnail: asset.thumbnailUrl,
                    name: asset.name
                };
            }
        }
        return null;
    });

    // State Persistence
    const BILLING_RESUME_PENDING_KEY = 'billingResumePending'
    const BILLING_RESUME_SECTION_KEY = 'billingResumeSectionId'
    const BILLING_RESUME_FALLBACK_SECTION = 'splash'
    const LOCAL_SUBMISSION_ID_PREFIX = 'local-'
    const DEFAULT_CAMPAIGN_NAME = 'Campaign'
    const DEFAULT_SERVICE_ID = 'tap-to-redeem' as CampaignService

    const loadState = () => {
      try {
        const saved = localStorage.getItem(getBuilderStorageKey())
        if (saved) {
          const parsedState = JSON.parse(saved)
          campaignBuilderStore.loadState(parsedState);
        }
      } catch (error) {
        console.warn('Failed to load state from localStorage:', error)
      }
    }

    const saveState = () => {
      try {
        localStorage.setItem(getBuilderStorageKey(), JSON.stringify(campaignBuilderStore.$state))
      } catch (error) {
        console.warn('Failed to save state to localStorage:', error)
      }
    }

    const loadCampaignForEdit = async () => {
      if (!editingCampaignId.value) {
        return
      }

      await experiencesService.syncLibrary(true)
      const draft = await campaignsService.getCampaignEditDraft(editingCampaignId.value)
      if (!draft?.questionPack) {
        throw new Error('This campaign cannot be re-edited because no saved experience draft was found.')
      }

      const builderState = buildBuilderDraftState(draft)
      campaignBuilderStore.$reset()
      campaignBuilderStore.loadState(builderState)
      campaignBuilderStore.updateSectionCompletion()

      const hydratedQuizConfig = template.value.quizConfig
        ? materializeExperienceIdentity(normalizeQuizConfig(template.value.quizConfig))
        : null

      if (hydratedQuizConfig) {
        campaignBuilderStore.setQuizConfig(hydratedQuizConfig)
        saveExperienceLibraryRecord({
          quizConfig: hydratedQuizConfig,
          campaignName: template.value.campaignName || undefined,
          ...orgStamp(),
        })

        const localKey = draft.attachedExperience?.localKey
          || draft.attachedExperience?.experienceId
          || hydratedQuizConfig.experienceDefinitionLocalKey
        const record = localKey
          ? (getExperienceLibraryRecord(localKey) || await experiencesService.getExperience(localKey))
          : null

        if (record?.status === 'published') {
          selectedExperienceDraftKey.value = record.localKey
          experienceAttachMode.value = 'library'
          template.value.attachedExperience = buildAttachedExperienceReference(record)
        } else {
          selectedExperienceDraftKey.value = ''
          experienceAttachMode.value = 'quick'
          template.value.attachedExperience = null
        }
      }

      formNavigationStore.setSections(buildSectionsForConfig(template.value.boundary, template.value.experience))
    }

    // Auto-save state changes and update completion
    watch(campaignBuilderStore, () => {
      saveState();
      campaignBuilderStore.updateSectionCompletion();
    }, { deep: true })

    // Watch boundary + experience + arTrackingType to compose the campaign business template serial ID
    watch(
      () => [template.value.boundary, template.value.experience, template.value.arTrackingType] as const,
      ([boundary, experience, arTrackingType]) => {
        if (boundary && experience) {
          const rewardType = (experience === 'ar' && arTrackingType === 'marker') ? 2 : 1;
          template.value.campaignBusinessTemplate = buildCampaignBusinessTemplate(boundary, experience, 1, rewardType);
        }
      },
      { immediate: true }
    );

    // Legacy: keep feature watcher for backward compat with old saved states
    watch(
      () => template.value.feature,
      (feature) => {
        if (feature && !template.value.boundary) {
          const tier = (authStore.userTier || 'freemium') as 'freemium' | 'premium' | 'enterprise';
          const newTemplate = getTemplate(tier, feature as CampaignFeature, DEFAULT_SERVICE_ID);
          if (newTemplate) {
            Object.assign(template.value, newTemplate);
          }
        }
      },
      { immediate: true }
    );

    watch(template, (newTemplate) => {
      const needsUpload = newTemplate.experience === 'ar' || newTemplate.experienceType === 'ar' || newTemplate.experienceType === 'Augmented Reality' || newTemplate.experienceType === 'Augmented Reality AND Map';
      if (
        needsUpload &&
        defaultArAssets.value.length > 0 &&
        !lookFeel.value.selectedDefaultAssets.ar &&
        !lookFeel.value.stagedAssets.glbModel
      ) {
        handleDefaultAssetSelect(defaultArAssets.value[0]);
      }
    }, { deep: true });

    // Cross-section completion tracking is now in the store

    // Helper function to get completion status for a section
    const getSectionCompletion = (sectionName: string): boolean => {
      const completionKey = sectionName === 'look-feel' ? 'lookFeel' :
                           sectionName === 'user-details' ? 'userDetails' :
                           sectionName as keyof typeof completion.value
      return completion.value[completionKey] || false
    }

    const isVoucherConfigComplete = (voucherConfig?: VoucherConfig | null) => {
      if (!voucherConfig || !voucherConfig.vouchers || voucherConfig.vouchers.length === 0) {
        return false
      }

      if (voucherConfig.mode === 'generated') {
        return Boolean(voucherConfig.generatedConfig)
      }

      return Boolean(
        voucherConfig.uploadedConfig &&
        voucherConfig.uploadedConfig.header === 'Vouchers' &&
        voucherConfig.uploadedConfig.rowCount > 0
      )
    }

    watch(() => completion.value.template, (isComplete) => {
      if (isComplete) {
        uiStore.completeStep('feature-selection');
      } else {
        uiStore.removeStep('feature-selection');
      }
    }, { immediate: true });

    watch(() => completion.value.message, (isComplete) => {
      if (isComplete) {
        uiStore.completeStep('message');
      } else {
        uiStore.removeStep('message');
      }
    }, { immediate: true });

    watch(
      [() => template.value.voucherConfig, () => template.value.experience],
      ([voucherConfig, experience]) => {
        if (!experience || !isVoucherConfigComplete(voucherConfig)) {
          uiStore.removeStep('voucher-config')
          return
        }

        uiStore.completeStep('voucher-config')
      },
      { deep: true, immediate: true }
    )

    watch(
      [() => completion.value.location, () => template.value.boundary],
      ([isComplete, boundary]) => {
        if (boundary !== 'geo') {
          uiStore.removeStep('location');
          return;
        }

        if (isComplete) {
          uiStore.completeStep('location');
        } else {
          uiStore.removeStep('location');
        }
      },
      { immediate: true }
    );

    watch(() => lookFeel.value, (newLookFeel) => {
      // Check for logo
      if (newLookFeel.stagedAssets.logo || newLookFeel.selectedDefaultAssets.logo) {
        uiStore.completeStep('logo-asset');
      } else {
        uiStore.removeStep('logo-asset');
      }
      // Check for map marker
      if (!isMapMarkerEnabled.value) {
        uiStore.removeStep('map-marker-asset');
      } else if (newLookFeel.stagedAssets.mapMarker || newLookFeel.selectedDefaultAssets.mapMarker) {
        uiStore.completeStep('map-marker-asset');
      } else {
        uiStore.removeStep('map-marker-asset');
      }
      // Check for AR asset
      if (newLookFeel.stagedAssets.glbModel || newLookFeel.selectedDefaultAssets.ar) {
        uiStore.completeStep('ar-asset');
      } else {
        uiStore.removeStep('ar-asset');
      }
    }, { deep: true, immediate: true });

    // Validate default quiz config on init so pre-configured quizzes auto-complete
    {
      const cfg = template.value.quizConfig
      if (cfg) {
        const normalized = normalizeQuizConfig(cfg)
        const allQuestions = normalized.rounds.flatMap(r => r.stages.flatMap(s => s.questions))
        const hasQuestions = allQuestions.length > 0
        const CHOICELESS_TYPES = ['free-text', 'rating', 'boolean']
        const isValid = hasQuestions && normalized.rounds.every((round) =>
          round.stages.every((stage) => stage.questions.every((question) => {
            if (!question.text.trim()) return false
            if (CHOICELESS_TYPES.includes(question.type || '')) return true
            const hasValidChoices = question.choices.length >= 2 && question.choices.every(c => c.label.trim() || c.imageAssetId?.trim())
            if (!hasValidChoices) return false
            if (normalized.experienceMode === 'survey' || stage.type === 'survey') return true
            return Boolean(question.correctChoiceId)
          }))
        )
        if (isValid) uiStore.completeStep('quiz-config')
      }
    }

    watch(() => schedule.value, (newSchedule) => {
      const now = new Date();
      now.setSeconds(0, 0); // Reset seconds and milliseconds for accurate comparison

      if (newSchedule.endDateTime) {
        const endDate = new Date(newSchedule.endDateTime);
        endDate.setSeconds(0, 0); // Reset for comparison

        if (endDate > now) {
          uiStore.completeStep('campaign-schedule');
        } else {
          uiStore.removeStep('campaign-schedule');
        }
      } else {
        uiStore.removeStep('campaign-schedule');
      }
    }, { deep: true });

    // Ensure the campaign window is long enough to cover every quiz slot's
    // scheduled start + attempt window. Backend rejects submissions where a
    // slot starts after the campaign end date ("slot-xxx starts after the
    // campaign end date"), so we extend the end date up to match — never
    // shorten. Runs whenever slots, start date, or attempt window changes.
    const attemptWindowDays = (window: QuizConfig['attemptWindow']): number => {
      if (window === 'day') return 1
      if (window === 'hour') return 1 / 24
      if (window === 'minute') return 1 / 1440
      return 7
    }

    watch(
      [
        () => quizConfig.value.slots,
        () => quizConfig.value.attemptWindow,
        () => schedule.value.startDateTime,
      ],
      () => {
        const slots = quizConfig.value.slots
        if (!slots?.length) return
        const startRaw = schedule.value.startDateTime
        if (!startRaw) return
        const startMs = new Date(startRaw).getTime()
        if (Number.isNaN(startMs)) return

        const windowMs = attemptWindowDays(quizConfig.value.attemptWindow) * 86400000
        let requiredEndMs = startMs + windowMs
        for (const slot of slots) {
          let slotStartMs = startMs
          if (slot.releaseType === 'offset' && typeof slot.offsetDays === 'number') {
            slotStartMs = startMs + slot.offsetDays * 86400000
          } else if (slot.releaseType === 'date' && slot.releaseDate) {
            const parsed = new Date(slot.releaseDate).getTime()
            if (!Number.isNaN(parsed)) slotStartMs = parsed
          }
          const slotEndMs = slotStartMs + windowMs
          if (slotEndMs > requiredEndMs) requiredEndMs = slotEndMs
        }

        const currentEndRaw = schedule.value.endDateTime
        const currentEndMs = currentEndRaw ? new Date(currentEndRaw).getTime() : 0
        if (requiredEndMs <= currentEndMs) return

        const newEnd = new Date(requiredEndMs)
        campaignBuilderStore.updateSchedule({
          endDateTime: newEnd,
          endDate: newEnd.toISOString().slice(0, 10),
          endTime: schedule.value.endTime || schedule.value.startTime || '23:59',
        })
      },
      { deep: true, immediate: true },
    );

    const handleTemplateSelection = (config: CampaignConfig) => {
      campaignBuilderStore.handleTemplateSelection(config);
    }

    const handleUploadComplete = (payload: { file: File, staged: boolean, localPreview?: string }, type: 'logo' | 'mapMarker' | 'ar' | 'background') => {
      campaignBuilderStore.stageFile(payload, type, activeBackgroundScreen.value);
    }

    const handleDefaultAssetSelect = (asset: DefaultAsset) => {
      campaignBuilderStore.selectDefaultAsset(asset, activeBackgroundScreen.value);
      isAssetModalVisible.value = false;
    };

    const handleAssetsUpdate = (assets: StagedAssets) => {
      Object.assign(lookFeel.value.uploadedAssets, assets)
    }

    const validateDropConfig = () => {
      // Ensure max claims per user is not less than drops per drop
      if (drops.value.maxClaimsPerUser < drops.value.dropsPerDrop) {
        campaignBuilderStore.updateDropConfig({ maxClaimsPerUser: drops.value.dropsPerDrop });
      }

      // For quiz/survey campaigns the legacy per-user claim cap (maxClaimsPerUser →
      // backend maxClaimSize) IS the configured "Max Attempts". Mirror it directly so
      // lowering Max Attempts lowers the cap. Previously this only raised the cap
      // (one-way floor), so a value below the maxClaimsPerUser default (3) never
      // propagated — set 2, player still got 3 (IRL2-2975). Floor at dropsPerDrop only
      // (a real constraint: you cannot claim fewer than one drop's worth).
      const quizConfig = template.value.quizConfig;
      if (quizConfig && (template.value.experience === 'quiz' || template.value.experience === 'survey')) {
        const cap = resolveMaxClaimsPerUser({
          experience: template.value.experience,
          attemptLimit: quizConfig.attemptLimit,
          maxClaimsPerUser: drops.value.maxClaimsPerUser,
          dropsPerDrop: drops.value.dropsPerDrop,
        });
        if (drops.value.maxClaimsPerUser !== cap) {
          campaignBuilderStore.updateDropConfig({ maxClaimsPerUser: cap });
        }
      }
    }

    const getRatioDescription = () => {
      const ratio = drops.value.winRatio
      if (ratio >= 80) return 'Very High'
      if (ratio >= 60) return 'High'
      if (ratio >= 40) return 'Medium'
      if (ratio >= 20) return 'Low'
      return 'Very Low'
    }

    const formatTimePeriod = () => {
      const period = drops.value.timePeriod
      switch (period) {
        case 1: return 'hour'
        case 6: return '6 hours'
        case 24: return 'day'
        case 168: return 'week'
        case 720: return 'month'
        default: return `${period} hours`
      }
    }

    const handleMessageUpdate = (newMessage: string) => {
      campaignBuilderStore.updateMessage(newMessage)
    }

    const handleVoucherConfigUpdate = (voucherConfig: VoucherConfig) => {
      template.value.voucherConfig = voucherConfig
      template.value.voucherCodes = voucherConfig.vouchers
      template.value.voucherIntegration =
        voucherConfig.mode === 'upload' ? 'upload-vouchers' : 'generated-vouchers'
      template.value.voucherUpload =
        voucherConfig.mode === 'upload'
          ? {
              actionType: 'uploadvouchers',
              delimiter: ',',
              mapping: {
                vouchers: 'Vouchers'
              },
              fileName: voucherConfig.uploadedConfig?.fileName
            }
          : undefined
    }

    const normalizeCtaConfig = (value?: CtaButtonConfig | null): CtaButtonConfig | undefined => {
      if (!value?.url) {
        return undefined
      }
      const url = stripButtonUrlProtocol(value.url)
      if (!url) {
        return undefined
      }
      const label = value.label?.trim()
      return {
        url,
        target: value.target === '_self' ? '_self' : '_blank',
        label: label || undefined
      }
    }

    const normalizeScreenButtonConfig = (
      screen: 'registration' | 'interaction' | 'prize',
      value?: StyledButtonConfig | null,
    ): StyledButtonConfig => {
      return normalizeStyledButtonConfig(value, DEFAULT_BUTTON_CONFIGS[screen])
    }

    const VOUCHER_VALIDITY_DAYS = 365
    const MAX_VOUCHER_NAME_LENGTH = 60
    const DEFAULT_GENERATED_VOUCHER_TYPE: GeneratedVoucherConfig['voucherType'] = 'single-use'
    const FALLBACK_VOUCHER_NAME_EMAIL = 'voucher'
    const VOUCHER_NAME_SEPARATOR = '-'

    const formatDateOnly = (date: Date): string => date.toISOString().split('T')[0]

    const sanitizeVoucherNameEmail = (email: string): string => {
      const normalizedEmail = email.trim().replace(/\s/g, '').toLowerCase()
      return normalizedEmail || FALLBACK_VOUCHER_NAME_EMAIL
    }

    const buildVoucherNameTimestamp = (date: Date): string => {
      return date.toISOString().replace(/[-:.TZ]/g, '').slice(0, 14)
    }

    const buildGeneratedVoucherName = (email: string, now: Date): string => {
      const emailPart = sanitizeVoucherNameEmail(email)
      const timestampPart = buildVoucherNameTimestamp(now)
      const generatedName = `${emailPart}${VOUCHER_NAME_SEPARATOR}${timestampPart}`
      return generatedName.slice(0, MAX_VOUCHER_NAME_LENGTH)
    }

    const buildDefaultVoucherValidUntil = (now: Date): string => {
      const validUntil = new Date(now)
      validUntil.setDate(validUntil.getDate() + VOUCHER_VALIDITY_DAYS)
      return formatDateOnly(validUntil)
    }

    const normalizeVoucherConfigForSubmission = (
      voucherConfig: VoucherConfig | null | undefined,
      email: string
    ): VoucherConfig | undefined => {
      if (!voucherConfig) {
        return undefined
      }
      if (voucherConfig.mode !== 'generated') {
        return voucherConfig
      }

      const now = new Date()
      return {
        ...voucherConfig,
        generatedConfig: {
          ...voucherConfig.generatedConfig,
          voucherName: buildGeneratedVoucherName(email, now),
          voucherType: DEFAULT_GENERATED_VOUCHER_TYPE,
          validUntil: buildDefaultVoucherValidUntil(now)
        }
      }
    }

    const handleCtaConfigUpdate = (ctaConfig?: CtaButtonConfig) => {
      template.value.ctaButton = normalizeCtaConfig(ctaConfig)
    }

    const handleScreenButtonConfigUpdate = (
      screen: 'registration' | 'interaction' | 'prize',
      buttonConfig: StyledButtonConfig,
    ) => {
      const currentColors = screenBrandColors.value[screen]
      const previousFallbacks = buildButtonFallbacks(currentColors)
      const resolvedColors = resolveButtonStyleColors(
        buttonConfig,
        DEFAULT_BUTTON_CONFIGS[screen],
        previousFallbacks,
      )

      if (resolvedColors.backgroundColor.trim().toUpperCase() !== currentColors.secondary.trim().toUpperCase()) {
        campaignBuilderStore.setScreenBrandColor(screen, 'secondary', resolvedColors.backgroundColor)
      }

      if (resolvedColors.textColor.trim().toUpperCase() !== currentColors.buttonText.trim().toUpperCase()) {
        campaignBuilderStore.setScreenBrandColor(screen, 'buttonText', resolvedColors.textColor)
      }

      const sanitizedButtonConfig = syncButtonColorOverridesWithFallbackChange(
        buttonConfig,
        DEFAULT_BUTTON_CONFIGS[screen],
        previousFallbacks,
        buildButtonFallbacks(screenBrandColors.value[screen]),
      )

      // IRL2-2343: cascade button *style* properties (variant, radius, blur,
      // drop-shadow) from registration to interaction/prize when they haven't
      // been independently customised (still match their defaults).
      const cascaded: Record<string, StyledButtonConfig> = {}
      if (screen === 'registration') {
        const STYLE_KEYS = ['styleVariant', 'borderRadius', 'blurPx', 'dropShadow'] as const
        const dependentScreens: Array<'interaction' | 'prize'> = ['interaction', 'prize']
        for (const dep of dependentScreens) {
          const existing = template.value.buttonConfigs?.[dep]
          const defaults = DEFAULT_BUTTON_CONFIGS[dep]
          const isAtDefaults = STYLE_KEYS.every((k) => {
            if (k === 'dropShadow') {
              return !existing?.dropShadow || existing.dropShadow.enabled === (defaults.dropShadow?.enabled ?? false)
            }
            return existing?.[k] === undefined || existing?.[k] === defaults[k]
          })
          if (isAtDefaults) {
            cascaded[dep] = {
              ...(existing ?? defaults),
              styleVariant: sanitizedButtonConfig.styleVariant,
              borderRadius: sanitizedButtonConfig.borderRadius,
              blurPx: sanitizedButtonConfig.blurPx,
              dropShadow: sanitizedButtonConfig.dropShadow
                ? { ...sanitizedButtonConfig.dropShadow }
                : undefined,
            }
          }
        }
      }

      template.value.buttonConfigs = {
        ...template.value.buttonConfigs,
        ...cascaded,
        [screen]: normalizeScreenButtonConfig(screen, sanitizedButtonConfig),
      }
      if (screen === 'prize' && buttonConfig.url) {
        template.value.ctaButton = normalizeCtaConfig({
          url: buttonConfig.url,
          target: buttonConfig.target,
          label: buttonConfig.label,
        })
      }
    }

    const updateMessageCount = () => {
    }

    const isNearLimit = () => {
      return message.value.text.length > message.value.maxLength * 0.9
    }

    const isAtLimit = () => {
      return message.value.text.length >= message.value.maxLength
    }

    const handleScheduleChange = (newSchedule: CampaignSchedule) => {
      campaignBuilderStore.updateSchedule(newSchedule);
    }

    const formatScheduledTime = () => {
      if (schedule.value.startDateTime) {
        const date = new Date(schedule.value.startDateTime)
        return date.toLocaleString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
      return null
    }

    const formatScheduledEndTime = () => {
      if (schedule.value.endDateTime) {
        const date = new Date(schedule.value.endDateTime)
        return date.toLocaleString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
      return null
    }

    const getTimeUntilLaunch = () => {
      if (!schedule.value.startDateTime) {
        return 'Select date and time to see countdown'
      }

      const now = new Date()
      const launch = new Date(schedule.value.startDateTime)
      const diff = launch.getTime() - now.getTime()

      if (diff <= 0) {
        return 'Campaign should start now'
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

      if (days > 0) {
        return `${days} days, ${hours} hours`
      } else if (hours > 0) {
        return `${hours} hours, ${minutes} minutes`
      } else {
        return `${minutes} minutes`
      }
    }

    const getCurrentTimezone = () => {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    }

    const formatInternationalPhoneNumber = (countryCode: string, localNumber: string) => {
      const codeDigits = (countryCode ?? '').replace(/[^\d]/g, '')
      const normalizedCode = codeDigits ? `+${codeDigits}` : ''
      const digits = (localNumber ?? '').replace(/\D/g, '').replace(/^0+/, '')

      if (!digits) {
        return ''
      }

      return `${normalizedCode}${digits}`
    }

    const getFeatureLabel = (featureId?: CampaignFeature | null) => {
      if (!featureId) {
        return ''
      }
      return FEATURE_OPTIONS.find(option => option.id === featureId)?.label ?? ''
    }

    const getExperienceLabel = () => {
      const exp = template.value.experience
      if (!exp) return ''
      return EXPERIENCE_OPTIONS.find(o => o.id === exp)?.label ?? ''
    }

    const getBoundaryLabel = () => {
      const b = template.value.boundary
      if (!b) return ''
      return BOUNDARY_OPTIONS.find(o => o.id === b)?.label ?? ''
    }

    const resolveCampaignName = (featureId?: CampaignFeature | null) => {
      if (template.value.campaignName?.trim()) return template.value.campaignName.trim()
      const userName = userDetails.value.name?.trim()
      if (userName) return `${userName}'s Campaign`
      const boundaryLabel = getBoundaryLabel()
      const expLabel = getExperienceLabel()
      if (boundaryLabel && expLabel) return `${boundaryLabel} ${expLabel}`
      const label = getFeatureLabel(featureId)
      return label || DEFAULT_CAMPAIGN_NAME
    }

    // Serialization for backend submission
    const serializeForBackend = (): FormSubmission => {
      const userPhoneNumber = formatInternationalPhoneNumber(
        userDetails.value.mobileCountryCode,
        userDetails.value.mobileNumber
      )
      const rawLocationConfig = template.value.boundary === 'geo'
        ? template.value.locationConfig
        : undefined
      const normalizedLocationConfig = rawLocationConfig
        ? normalizeLocationConfig(rawLocationConfig)
        : undefined
      const mapMarkerS3Path = isMapMarkerEnabled.value
        ? lookFeel.value.uploadedAssets.mapMarker?.s3Path ?? ''
        : ''
      const backgroundImageS3Paths = BACKGROUND_SCREEN_KEYS.reduce((acc, screen) => {
        const assetKey = getBackgroundAssetKey(screen)
        acc[screen] = lookFeel.value.uploadedAssets[assetKey]?.s3Path ?? ''
        return acc
      }, {} as Record<BackgroundScreenKey, string>)
      const assetsToValidate = [
        lookFeel.value.uploadedAssets.logo,
        lookFeel.value.uploadedAssets.glbModel,
        ...(isMapMarkerEnabled.value ? [lookFeel.value.uploadedAssets.mapMarker] : []),
        ...BACKGROUND_SCREEN_KEYS
          .map((screen) => lookFeel.value.uploadedAssets[getBackgroundAssetKey(screen)])
          .filter(Boolean),
      ].filter(Boolean)
      const assetsApproved = assetsToValidate.every(
        asset => asset && asset.bucketType === 'approved'
      )
      const voucherConfig = normalizeVoucherConfigForSubmission(template.value.voucherConfig, userDetails.value.email)
      const resolvedPrizeButton = normalizeScreenButtonConfig('prize', template.value.buttonConfigs?.prize)
      const ctaButton = normalizeCtaConfig(
        template.value.ctaButton ?? (
          resolvedPrizeButton.url
            ? {
              url: resolvedPrizeButton.url,
              target: resolvedPrizeButton.target,
              label: resolvedPrizeButton.label
            }
            : undefined
        )
      )
      const ctaButtonForSubmission = ctaButton
        ? {
          ...ctaButton,
          url: ensureHttpsButtonUrl(ctaButton.url) ?? ctaButton.url,
        }
        : undefined
      const buttonConfigs = {
        registration: normalizeScreenButtonConfig('registration', template.value.buttonConfigs?.registration),
        interaction: normalizeScreenButtonConfig('interaction', template.value.buttonConfigs?.interaction),
        prize: {
          ...resolvedPrizeButton,
          url: ensureHttpsButtonUrl(ctaButton?.url ?? resolvedPrizeButton.url) ?? undefined,
          target: ctaButtonForSubmission?.target ?? resolvedPrizeButton.target,
          label: ctaButtonForSubmission?.label ?? resolvedPrizeButton.label,
        }
      }
      const voucherCodes = voucherConfig?.vouchers ?? template.value.voucherCodes ?? []
      const voucherIntegration = voucherConfig
        ? voucherConfig.mode === 'upload'
          ? 'upload-vouchers'
          : 'generated-vouchers'
        : template.value.voucherIntegration
      const screenAppearances = SCREEN_KEYS.reduce((acc, screen) => {
        const colors = screenBrandColors.value[screen]
        const backgroundImageUrl =
          screen === 'registration' || screen === 'prize'
            ? backgroundImageS3Paths[screen]
            : ''
        acc[screen] = {
          backgroundColor: hexToRgba(colors.background),
          headerColor: hexToRgba(colors.header),
          secondaryColor: hexToRgba(colors.secondary),
          buttonTextColor: hexToRgba(colors.buttonText),
          ...(backgroundImageUrl
            ? { backgroundImageUrl }
            : {}),
        }
        return acc
      }, {} as ScreenAppearanceConfigs)

      // Colour bridge — when the survey uses the new "africa" template, map the
      // campaign's Interaction edit-colours into the quizConfig branding. accent
      // + surface ride the existing experience-init pipe today (primaryColor /
      // backgroundColor); the full set is stashed in surveyBranding for the
      // (gated) backend pass-through. The classic template is left untouched.
      const baseQuizConfig = template.value.quizConfig ?? null
      let quizConfigForSubmission = baseQuizConfig
      if (
        baseQuizConfig &&
        template.value.experience === 'survey' &&
        baseQuizConfig.surveyTemplateVariant === 'africa'
      ) {
        const ix = screenBrandColors.value.interaction
        quizConfigForSubmission = {
          ...baseQuizConfig,
          primaryColor: ix.secondary,
          backgroundColor: ix.background,
          surveyBranding: {
            accentColor: ix.secondary,
            surfaceColor: ix.background,
            buttonTextColor: ix.buttonText,
            textColor: ix.header,
          },
        }
      }

      return {
        user: {
          name: userDetails.value.name,
          surname: userDetails.value.surname,
          email: userDetails.value.email,
          mobileNumber: userPhoneNumber,
          cognitoUserId: authStore.userId || undefined,
          marketingConsent: String(userDetails.value.marketingConsent) as "true" | "false",
          acceptedTermsAndConditions: String(userDetails.value.consent) as "true" | "false",
          acceptedPrivacyPolicy: String(userDetails.value.consent) as "true" | "false"
        },
        tier: 'freemium' as const,

        // Campaign configuration
        campaignConfig: {
          campaignName: resolveCampaignName(template.value.feature),
          // Template configuration
          experienceType: template.value.experienceType,
          redeemType: template.value.redeemType,
          voucherIntegration,
          voucherConfig,
          ctaButton: ctaButtonForSubmission,
          buttonConfigs,
          voucherCodes,
          voucherUpload: template.value.voucherUpload,
          finalAssetType: template.value.finalAssetType,
          campaignBusinessTemplate: template.value.campaignBusinessTemplate,
          arTrackingType: template.value.arTrackingType,
          ...(template.value.experience === 'ar' && template.value.arTrackingType === 'marker' && campaignBuilderStore.markerTarget.sourceImageUrl
            ? { markerImageUrl: campaignBuilderStore.markerTarget.sourceImageUrl }
            : {}),
          ...(template.value.experience === 'ar' && template.value.arTrackingType === 'marker' && campaignBuilderStore.markerTarget.mindFileUrl
            ? { markerMindFileUrl: campaignBuilderStore.markerTarget.mindFileUrl }
            : {}),

          // Brand customization
          primaryColor: hexToRgba(registrationBrandColors.value.secondary),
          secondaryColor: hexToRgba(registrationBrandColors.value.secondary),
          backgroundColor: hexToRgba(registrationBrandColors.value.background),
          accentColor: hexToRgba(registrationBrandColors.value.secondary),
          headerColor: hexToRgba(registrationBrandColors.value.header),
          prizeMessageColor: hexToRgba(prizeBrandColors.value.header),
          buttonTextColor: hexToRgba(registrationBrandColors.value.buttonText),
          googleFont: lookFeel.value.googleFont,
          screenAppearances,

          // Drop mechanics — for quiz/survey the legacy per-user claim cap mirrors the
          // configured "Max Attempts" directly (IRL2-2975). Previously Math.max floored it
          // against the maxClaimsPerUser default, so a value below the default never saved.
          dropsPerDrop: drops.value.dropsPerDrop,
          maxClaimsPerUser: resolveMaxClaimsPerUser({
            experience: template.value.experience,
            attemptLimit: template.value.quizConfig?.attemptLimit,
            maxClaimsPerUser: drops.value.maxClaimsPerUser,
            dropsPerDrop: drops.value.dropsPerDrop,
          }),
          timePeriod: drops.value.timePeriod,
          winRatio: drops.value.winRatio,

          // Prize message
          prizeMessage: message.value.text,

          // Asset URLs (if approved)
          logoUrl: lookFeel.value.uploadedAssets.logo?.s3Path,
          arModelUrl: lookFeel.value.uploadedAssets.glbModel?.s3Path,
          mapMarkerUrl: mapMarkerS3Path,
          ...(backgroundImageS3Paths.registration ? { backgroundImageUrl: backgroundImageS3Paths.registration } : {}),
          faviconUrl: defaultFaviconAsset.s3Path,
          assetsApproved,
          
          // Location Configuration
          locationConfig: normalizedLocationConfig,

          // Shared published experience attachment
          attachedExperience: experienceAttachMode.value === 'library'
            ? buildAttachedExperienceReference(selectedAttachedExperience.value)
            : null,

          // Quiz Configuration (quiz feature only)
          ...((template.value.experience === 'quiz' || template.value.experience === 'survey') ? { quizConfig: quizConfigForSubmission } : {})
        },

        // Campaign schedule
        campaignSchedule: {
          startTime: schedule.value.startTime,
          startDate: schedule.value.startDate,
          startDateTime: schedule.value.startDateTime ? new Date(schedule.value.startDateTime).toISOString() : null,
          endTime: schedule.value.endTime,
          endDate: schedule.value.endDate,
          endDateTime: schedule.value.endDateTime ? new Date(schedule.value.endDateTime).toISOString() : null,
          timezone: schedule.value.timezone || getCurrentTimezone()
        },

        // Assets with full details
        assets: {
          logoUrl: lookFeel.value.uploadedAssets.logo?.s3Url || '',
          arModelUrl: lookFeel.value.uploadedAssets.glbModel?.s3Url || '',
          mapMarkerUrl: isMapMarkerEnabled.value ? lookFeel.value.uploadedAssets.mapMarker?.s3Url || '' : '',
          logoS3Path: lookFeel.value.uploadedAssets.logo?.s3Path || '',
          mapMarkerS3Path: mapMarkerS3Path,
          logoBucketType: lookFeel.value.uploadedAssets.logo?.bucketType === 'approved' ? 'approved' : 'pending',
          mapMarkerBucketType: lookFeel.value.uploadedAssets.mapMarker?.bucketType === 'approved' ? 'approved' : 'pending',
          ...(lookFeel.value.uploadedAssets.registrationBackground
            ? {
              backgroundImageUrl: lookFeel.value.uploadedAssets.registrationBackground.s3Url || '',
              backgroundImageS3Path: backgroundImageS3Paths.registration,
              backgroundImageBucketType: lookFeel.value.uploadedAssets.registrationBackground.bucketType === 'approved' ? 'approved' : 'pending',
            }
            : {}),
        }
      }
    }

    const BASE_REQUIRED_STEPS = [
      'feature-selection',
      'voucher-config',
      'logo-asset',
      'map-marker-asset',
      'ar-asset',
      'message',
      'campaign-schedule',
      'account-details',
      'profile-details'
    ] as const

    const requiredSteps = computed(() => {
      const exp = template.value.experience
      if (exp === 'quiz' || exp === 'survey') {
        return BASE_REQUIRED_STEPS.filter(
          step => step !== 'ar-asset' && step !== 'voucher-config' && step !== 'map-marker-asset'
        ).concat(['quiz-config'] as any)
      }
      if (template.value.boundary === 'geo') {
        return [...BASE_REQUIRED_STEPS, 'location'] as const
      }
      if (!isMapMarkerEnabled.value) {
        return BASE_REQUIRED_STEPS.filter(step => step !== 'map-marker-asset')
      }
      return BASE_REQUIRED_STEPS
    })

    // Validation helpers - use required steps completion as the source of truth
    const isFormComplete = computed(() => {
      return requiredSteps.value.every(step => uiStore.completedSteps.has(step))
    })

    const getCompletionPercentage = computed(() => {
      return uiStore.progressPercent
    })

    const updateTotalSteps = () => {
      uiStore.setTotalSteps(requiredSteps.value.length)
    }

    const clearState = () => {
      localStorage.removeItem(getBuilderStorageKey())
      location.reload()
    }

    // Campaign Submission
    const PREPARING_CAMPAIGN_MESSAGE = 'Preparing your campaign...'
    const CHECKING_ORG_SETUP_MESSAGE = 'Checking your organization setup...'
    const isSubmitting = ref(false)
    const submitSuccess = ref(false)
    const lastSubmissionId = ref<string | null>(null)
    const isRekognizing = ref(false);

    watch(
      isSubmitting,
      (submitting) => {
        formNavigationStore.setScrollLock('submitting', submitting)
      },
      { immediate: true }
    )

    const showSubmitError = (message: string) => {
      uiStore.showError({
        title: 'Submission Error',
        message: message
      });
    };

    const startOtpFlow = (username: string, password: string, email: string) => {
      pendingVerification.username = username
      pendingVerification.password = password
      pendingVerification.email = email
      pendingVerification.code = ''
      pendingVerification.error = ''
      pendingVerification.isSubmitting = false
      pendingVerification.isResending = false
      uiStore.stopLoading()
      showVerificationModal.value = true

      return new Promise<boolean>((resolve) => {
        pendingVerification.resolver = resolve
      })
    }

    const resolveOtpFlow = (result: boolean) => {
      if (pendingVerification.resolver) {
        pendingVerification.resolver(result)
        pendingVerification.resolver = null
      }
    }

    const ensureAuthenticated = async () => {
      
      // FIX: Remove all whitespace from email to prevent "space in email" errors
      const email = (userDetails.value.email || '').trim().replace(/\s/g, '').toLowerCase()
      
      // Update the store to reflect the sanitized email (auto-correct UI)
      if (userDetails.value.email !== email) {
          updateUserDetails({ email })
      }

      const password = userDetails.value.password || ''

      // If already authenticated, just ensure verification
      if (authStore.isAuthenticated) {
        if (!authStore.isEmailVerified) {
             const verified = await startOtpFlow(authStore.userDetails.email || email, password, email)
             if (!verified) {
               showSubmitError('Please verify your email to continue.')
               return false
             }
             if (!uiStore.isLoading) uiStore.startLoading(PREPARING_CAMPAIGN_MESSAGE)
             await authStore.checkAuth()
        }
        return true
      }

      // Validation for new/unauthenticated users
      if (!email || !password) {
        showSubmitError('Email and password are required to continue.')
        return false
      }

      if (userDetails.value.confirmPassword && userDetails.value.confirmPassword !== password) {
        showSubmitError('Passwords do not match.')
        return false
      }

      // Generate a preferred username from first + surname (fallback to email)
      const username = buildPreferredUsername(userDetails.value.name, userDetails.value.surname, email)
      
      const userPhoneNumber = formatInternationalPhoneNumber(
        userDetails.value.mobileCountryCode,
        userDetails.value.mobileNumber
      )

      // Login-First Strategy
      const cognitoUsername = email // Cognito expects email as username
      
      try {
        await authStore.login(email, password)
        await authStore.checkAuth()

        if (!authStore.isAuthenticated) {
          showSubmitError('Unable to log in. Please check your details.')
          return false
        }
        
        // If login succeeded, check verification
        if (authStore.isAuthenticated && !authStore.isEmailVerified) {
            // Resend code if needed
            try { await authStore.resendVerification(email) } catch (e) { console.warn('Resend failed', e) }
            
            const verified = await startOtpFlow(email, password, email)
            if (!verified) {
                showSubmitError('Please verify your email to continue.')
                return false
            }
            if (!uiStore.isLoading) uiStore.startLoading(PREPARING_CAMPAIGN_MESSAGE)
            await authStore.checkAuth()
        }
        return true

      } catch (loginError: unknown) {
        const extractAuthErrorInfo = (error: unknown) => {
          if (!error || typeof error !== 'object') {
            return { type: '', message: '' }
          }

          const err = error as {
            name?: string
            code?: string
            __type?: string
            message?: string
            cause?: unknown
          }
          const cause = err.cause as { name?: string; code?: string; __type?: string; message?: string } | undefined
          const type = err.__type || err.code || err.name || cause?.__type || cause?.code || cause?.name || ''
          const message = err.message || cause?.message || ''

          return { type, message }
        }

        const { type: authErrorType, message: authErrorMessage } = extractAuthErrorInfo(loginError)
        const normalizedMessage = authErrorMessage.toLowerCase()

        
        // Check if it's because they are unconfirmed (User exists but not verified)
        const isUnconfirmed = authErrorType === 'UserNotConfirmedException' ||
                              normalizedMessage.includes('not confirmed')

        if (isUnconfirmed) {
            try {
                const signUpResult = await authStore.register(email, password, username, userPhoneNumber)

                if (signUpResult?.isSignUpComplete) {
                    if (!authStore.isAuthenticated) {
                      await authStore.login(cognitoUsername, password)
                      await authStore.checkAuth()
                    }
                    if (!authStore.isAuthenticated) {
                      showSubmitError('Account confirmed, but login failed. Please try again.')
                      return false
                    }
                    return true
                }

                const verified = await startOtpFlow(cognitoUsername, password, email)
                if (!verified) {
                    showSubmitError('Please verify your email to continue.')
                    return false
                }
                if (!uiStore.isLoading) uiStore.startLoading(PREPARING_CAMPAIGN_MESSAGE)
                if (!authStore.isAuthenticated) {
                  await authStore.login(cognitoUsername, password)
                  await authStore.checkAuth()
                }
                if (!authStore.isAuthenticated) {
                  showSubmitError('Verification succeeded, but login failed. Please try again.')
                  return false
                }
                return true
            } catch (signUpError: unknown) {
                const sErr = signUpError as { name?: string; code?: string; message?: string }
                if (sErr?.name === 'UsernameExistsException' || sErr?.code === 'UsernameExistsException') {
                    try { await authStore.resendVerification(email) } catch (e) { console.warn('Resend failed', e) }
                    const verified = await startOtpFlow(email, password, email)
                    if (!verified) {
                        showSubmitError('Please verify your email to continue.')
                        return false
                    }
                    if (!uiStore.isLoading) uiStore.startLoading(PREPARING_CAMPAIGN_MESSAGE)
                    if (!authStore.isAuthenticated) {
                      await authStore.login(email, password)
                      await authStore.checkAuth()
                    }
                    if (!authStore.isAuthenticated) {
                      showSubmitError('Verification succeeded, but login failed. Please try again.')
                      return false
                    }
                    return true
                }

                console.error('Registration flow failed', sErr)
                showSubmitError(sErr?.message || 'Unable to create account. Please check your details.')
                return false
            }
        }

        // Check if we should try to register (User Not Found OR Wrong Password masked as NotAuthorized)
        const shouldTryRegister = 
            authErrorType === 'UserNotFoundException' || 
            authErrorType === 'NotAuthorizedException';

        if (shouldTryRegister) {
            try {
                const signUpResult = await authStore.register(email, password, username, userPhoneNumber)
                
                // Handle Successful Signup (New User)
                if (signUpResult) {
                    if (signUpResult.isSignUpComplete) {
                        // Auto login
                        if (!authStore.isAuthenticated) {
                          await authStore.login(cognitoUsername, password)
                          await authStore.checkAuth()
                        }
                        if (!authStore.isAuthenticated) {
                          showSubmitError('Account created, but login failed. Please try again.')
                          return false
                        }
                        return true
                    } else {
                        // Need verification
                        const verified = await startOtpFlow(cognitoUsername, password, email)
                        if (!verified) {
                            showSubmitError('Please enter the verification code we emailed you.')
                            return false
                        }
                        if (!uiStore.isLoading) uiStore.startLoading(PREPARING_CAMPAIGN_MESSAGE)
                        if (!authStore.isAuthenticated) {
                          await authStore.login(cognitoUsername, password)
                          await authStore.checkAuth()
                        }
                        if (!authStore.isAuthenticated) {
                          showSubmitError('Verification succeeded, but login failed. Please try again.')
                          return false
                        }
                        return true
                    }
                }
            } catch (signUpError: unknown) {
                const sErr = signUpError as { name?: string; code?: string; message?: string };
                // If registration fails with UsernameExistsException, it means the user DID exist,
                // so the initial login failure was due to a WRONG PASSWORD.
                if (sErr?.name === 'UsernameExistsException' || sErr?.code === 'UsernameExistsException') {
                    showSubmitError('Account exists. The password you entered is incorrect.')
                    return false
                }
                
                // Real registration error
                console.error('Register error', sErr)
                showSubmitError(sErr?.message || 'Unable to create account. Please check your details.')
                return false
            }
        }

        // Other login errors
        showSubmitError(authErrorMessage || 'Unable to log in.')
        return false
      }
    }

    const ensureProvisioningReadyForSubmission = async () => {
      try {
        uiStore.startLoading(CHECKING_ORG_SETUP_MESSAGE)
        await waitForProvisioningReady({
          maxAttempts: 10,
          pollIntervalMs: 3000
        })
        return true
      } catch (error) {
        console.error('Provisioning readiness check failed:', error)
        showSubmitError(
          error instanceof Error
            ? error.message
            : 'Your organization is still being provisioned. Please try again shortly.'
        )
        return false
      }
    }

    const submitCampaign = async () => {
      if (isSubmitting.value || isRekognizing.value) return

      // Validate that all required sections are complete. When incomplete,
      // surface EXACTLY what's missing — walking every section so the user
      // sees every blocker at once rather than hitting them one-by-one on
      // subsequent submits. Closes IRL2-2346.
      if (!isFormComplete.value) {
        const allMissing = sections.value
          .map((sectionId) => ({
            sectionId,
            fields: getMissingFieldsForSection(sectionId),
          }))
          .filter((entry) => entry.fields.length > 0)

        if (allMissing.length > 0) {
          const lines = allMissing.map(({ sectionId, fields }) => {
            const sectionLabel = SECTION_LABELS[sectionId] ?? sectionId
            return `• ${sectionLabel}: ${fields.join(', ')}`
          })
          showSubmitError(
            `Before you can launch, please complete:\n${lines.join('\n')}`
          )
        } else {
          // Fallback — isFormComplete said incomplete but we couldn't
          // resolve individual blockers (stale completion flags, custom
          // section, etc.). Keep the generic prompt so users aren't stuck.
          showSubmitError('Please complete all required sections before submitting.')
        }
        return
      }

      // Marker-mode integrity check. Only enforced for AR campaigns —
      // arTrackingType defaults to 'marker' on all campaigns so this must
      // gate on experience === 'ar' too, otherwise non-AR campaigns
      // (quiz, survey, etc.) get incorrectly blocked on a marker upload.
      if (template.value.experience === 'ar' && template.value.arTrackingType === 'marker') {
        const marker = campaignBuilderStore.markerTarget
        const hasSource = Boolean(marker?.sourceImage) || Boolean(marker?.sourceImageUrl)
        if (!hasSource) {
          showSubmitError('Marker tracking is selected — please upload a marker image before submitting.')
          return
        }
      }

      submitSuccess.value = false
      isSubmitting.value = true
      
      // Start global loading
      uiStore.startLoading(PREPARING_CAMPAIGN_MESSAGE);

      try {
        const isAuthed = await ensureAuthenticated()
        if (!isAuthed) {
          isSubmitting.value = false
          uiStore.stopLoading()
          return
        }

        const isProvisioningReady = await ensureProvisioningReadyForSubmission()
        if (!isProvisioningReady) {
          isSubmitting.value = false
          uiStore.stopLoading()
          return
        }

        // Step 1: Upload staged files first
        const stagedAssets = lookFeel.value.stagedAssets;
        const uploadPromises: Array<Promise<UploadResult>> = [];
        const assetUploads: Array<{ type: 'logo' | 'mapMarker' | 'ar' | 'background' | 'marker'; screen?: BackgroundScreenKey }> = [];

        if (stagedAssets.logo?.file?.name) {
          const unifiedFileName = `logo-${Date.now()}-${stagedAssets.logo.file.name}`;
          uploadPromises.push(ApiService.uploadFile(stagedAssets.logo.file, 'logo', unifiedFileName));
          assetUploads.push({ type: 'logo' });
        }
        if (stagedAssets.mapMarker?.file?.name) {
          const unifiedFileName = `map-marker-${Date.now()}-${stagedAssets.mapMarker.file.name}`;
          uploadPromises.push(ApiService.uploadFile(stagedAssets.mapMarker.file, 'map-marker', unifiedFileName));
          assetUploads.push({ type: 'mapMarker' });
        }
        if (stagedAssets.glbModel?.file?.name) {
          const unifiedFileName = `ar-asset-${Date.now()}-${stagedAssets.glbModel.file.name}`;
          uploadPromises.push(ApiService.uploadFile(stagedAssets.glbModel.file, 'ar-asset', unifiedFileName));
          assetUploads.push({ type: 'ar' });
        }
        for (const screen of BACKGROUND_SCREEN_KEYS) {
          const assetKey = getBackgroundAssetKey(screen);
          const stagedBackground = stagedAssets[assetKey];
          if (!stagedBackground?.file?.name) {
            continue;
          }

          const unifiedFileName = `${screen}-background-${Date.now()}-${stagedBackground.file.name}`;
          uploadPromises.push(ApiService.uploadFile(stagedBackground.file, 'background', unifiedFileName));
          assetUploads.push({ type: 'background', screen });
        }

        // Marker source image — only when marker tracking is active and the
        // user has a fresh File (either newly selected, or a staged file
        // after we restored from localStorage). If edit-mode left the marker
        // with a URL but no File, we skip upload/compile and reuse the
        // existing .mind file later.
        //
        // We resize the source ONCE up front and keep the HTMLImageElement
        // around so the MindAR compile step (which runs after Rekognition
        // passes) can work from the same pixels we uploaded.
        let markerResizedImage: HTMLImageElement | null = null
        if (
          template.value.experience === 'ar' &&
          template.value.arTrackingType === 'marker' &&
          campaignBuilderStore.markerTarget.sourceImage
        ) {
          try {
            campaignBuilderStore.setMarkerCompileStatus('validating')
            const prepared = await prepareMarkerUpload(campaignBuilderStore.markerTarget.sourceImage)
            markerResizedImage = prepared.image
            uploadPromises.push(ApiService.uploadFile(prepared.file, 'logo', prepared.file.name))
            assetUploads.push({ type: 'marker' })
          } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to prepare marker image'
            campaignBuilderStore.setMarkerCompileError(message)
            uiStore.stopLoading()
            showSubmitError(`Marker image could not be prepared: ${message}`)
            isSubmitting.value = false
            return
          }
        }

        let uploadResults: UploadResult[] = [];
        if (uploadPromises.length > 0) {
          // Use allSettled to handle individual upload failures
          const uploadSettledResults = await Promise.allSettled(uploadPromises);
          
          // Check for any failures
          const failedIndex = uploadSettledResults.findIndex(result => result.status === 'rejected');
          
          if (failedIndex !== -1) {
            // ... (Handle upload error as before)
            // Handle upload error
            const failedResult = uploadSettledResults[failedIndex];
            const uploadError = (failedResult as PromiseRejectedResult).reason as ApiUploadError;
            const failedType = assetUploads[failedIndex]?.type ?? 'background';
            
            console.error('❌ Upload failed:', uploadError);
            
            // Check if this is a content validation error (400 status with moderation labels)
            if (uploadError.status === 400 && uploadError.details) {
              const errorDetails = uploadError.details;
              const moderationLabels = errorDetails.details || [];
              
              // Show Rekognition modal with error details
              isRekognizing.value = true;
              rekognitionResult.value = null;
              
              uiStore.stopLoading(); // Stop loading before showing modal
              showRekognitionModal.value = true;
              
              rekognitionResult.value = {
                isValid: false,
                confidence: 0,
                moderationLabels: moderationLabels,
                labels: [],
                assetType: failedType,
                errorInfo: {
                  errorType: 'CONTENT_VALIDATION_FAILED',
                  userMessage: errorDetails.message || 'Image failed content validation',
                  shouldRetry: false,
                  timestamp: new Date().toISOString()
                }
              };
              isRekognizing.value = false;
              
              // Wait for user to acknowledge the error (click "Reupload Asset")
              await new Promise<void>((resolve) => {
                rekognitionAcknowledgeResolver.value = resolve;
              });
              
              // After acknowledgment, stop submission - error stays in modal, not form view
              return; // Stop submission
            } else {
              // Other upload errors - throw to be caught by outer catch
              throw uploadError;
            }
          }
          
          // All uploads succeeded - extract results
          uploadResults = uploadSettledResults.map(result => (result as PromiseFulfilledResult<UploadResult>).value);

          // Step 2: Check Rekognition results from upload (no separate validation needed)
          isRekognizing.value = true;
          rekognitionResult.value = null;
          
          // Check if any uploads failed Rekognition validation
          const failedResult = uploadResults.find(result => 
            result.rekognitionResult && 
            (!result.rekognitionResult.isValid || 
             (result.rekognitionResult.errorInfo && !result.rekognitionResult.errorInfo.shouldRetry))
          );

          if (failedResult) {
             uiStore.stopLoading(); // Stop loading before showing modal
             showRekognitionModal.value = true;
            const failedIndex = uploadResults.indexOf(failedResult);
            const failedType = assetUploads[failedIndex]?.type ?? 'background';
            
            if (failedResult.rekognitionResult) {
              rekognitionResult.value = { 
                isValid: failedResult.rekognitionResult.isValid,
                confidence: failedResult.rekognitionResult.confidence,
                labels: failedResult.rekognitionResult.labels || [],
                moderationLabels: failedResult.rekognitionResult.moderationLabels || [],
                errorInfo: failedResult.rekognitionResult.errorInfo,
                assetType: failedType 
              };
            }
            isRekognizing.value = false;
            // Keep modal open - error stays in modal, not form view
            
            // Wait for user to acknowledge the error (click "Reupload Asset")
            await new Promise<void>((resolve) => {
              rekognitionAcknowledgeResolver.value = resolve;
            });
            
            // After acknowledgment, stop submission - error stays in modal, not form view
            return; // Stop submission
          }

          // All validations passed - store uploaded asset details
          uploadResults.forEach((result, index) => {
            const assetUpload = assetUploads[index];
            const assetType = assetUpload.type;
            // Marker source image writes to markerTarget, not to the
            // generic uploadedAssets slot.
            if (assetType === 'marker') {
              campaignBuilderStore.setMarkerSourceImageUrl(result.s3Path);
              return;
            }
            const assetKey =
              assetType === 'ar'
                ? 'glbModel'
                : assetType === 'background'
                  ? getBackgroundAssetKey(assetUpload.screen ?? 'registration')
                  : assetType;
            const stagedAsset = stagedAssets[assetKey as keyof StagedAssets];
            const assetDetails = {
              s3Url: result.s3Url,
              s3Path: result.s3Path,
              fileName: result.fileInfo?.originalName || stagedAsset?.fileName,
              fileSize: result.fileInfo?.size || stagedAsset?.fileSize || 0,
              fileType: result.fileInfo?.type || stagedAsset?.fileType || '',
              uploadTimestamp: new Date(),
              bucketType: result.rekognitionResult?.isValid ? 'approved' : 'pending'
            };

            if (assetType === 'logo') {
              campaignBuilderStore.updateUploadedAssets('logo', assetDetails);
            } else if (assetType === 'mapMarker') {
              campaignBuilderStore.updateUploadedAssets('mapMarker', assetDetails);
            } else if (assetType === 'ar') {
              campaignBuilderStore.updateUploadedAssets('ar', assetDetails);
            } else if (assetType === 'background') {
              campaignBuilderStore.updateUploadedAssets('background', assetDetails, assetUpload.screen ?? 'registration');
            }
          });

          // Aggregate Rekognition results from all uploads for display
          const rekognitionResults = uploadResults
            .map((result, index) => {
              if (result.rekognitionResult) {
                const r = result.rekognitionResult;
                return {
                  isValid: r.isValid,
                  confidence: r.confidence,
                  labels: r.labels || [],
                  moderationLabels: r.moderationLabels || [],
                  errorInfo: r.errorInfo,
                  assetType: assetUploads[index]?.type ?? 'background'
                } as RekognitionResult;
              }
              return null;
            })
            .filter((r): r is RekognitionResult => r !== null);

          if (rekognitionResults.length === 0) {
            // No Rekognition results (e.g., only GLB files) - skip modal and continue
            showRekognitionModal.value = false;
            isRekognizing.value = false;
          } else if (rekognitionResults.length === 1) {
            // Single asset - show its result directly
            rekognitionResult.value = rekognitionResults[0];
            isRekognizing.value = false;
            
            uiStore.stopLoading(); // Stop loading before showing modal
            showRekognitionModal.value = true; // Ensure modal is shown

            // Wait for user to acknowledge the feedback
            await new Promise<void>((resolve) => {
              rekognitionAcknowledgeResolver.value = resolve;
            });
            
            // Resume loading if we continue (though typically we might wait for the user to close the modal)
            // But here the flow halts until acknowledge.
            // If the user acknowledges and it's valid, we proceed. We should restart loading?
            // The logic: "handleRekognitionAcknowledge" resolves the promise.
            // If valid, we continue to Step 3.
            if (rekognitionResult.value?.isValid) {
                 uiStore.startLoading('Finalizing campaign...');
            }

          } else {
            // Multiple assets - aggregate results
            const allLabels = new Set<string>();
            const allModerationLabels = new Set<string>();
            let totalConfidence = 0;
            let validCount = 0;

            rekognitionResults.forEach(r => {
              if (r && r.labels) {
                r.labels.forEach((label: string) => allLabels.add(label));
              }
              if (r && r.moderationLabels) {
                r.moderationLabels.forEach((label: string) => allModerationLabels.add(label));
              }
              if (r && r.confidence !== undefined && r.confidence !== null) {
                totalConfidence += r.confidence;
                validCount++;
              }
            });

            rekognitionResult.value = {
              isValid: true,
              confidence: validCount > 0 ? totalConfidence / validCount : 100,
              labels: Array.from(allLabels),
              moderationLabels: Array.from(allModerationLabels),
              assetType: `${rekognitionResults.length} Assets`
            };
            isRekognizing.value = false;
            
            uiStore.stopLoading(); // Stop loading before showing modal
            showRekognitionModal.value = true;

            // Wait for user to acknowledge the feedback
            await new Promise<void>((resolve) => {
              rekognitionAcknowledgeResolver.value = resolve;
            });
            
             if (rekognitionResult.value?.isValid) {
                 uiStore.startLoading('Finalizing campaign...');
            }
          }
        } else if (
          [
            lookFeel.value.selectedDefaultAssets.logo,
            lookFeel.value.selectedDefaultAssets.mapMarker,
            lookFeel.value.selectedDefaultAssets.ar,
          ].every(v => v === null)
        ) {
          // No assets to upload, and no default assets selected, set defaults to "default" string
          const defaultAsset = {
            s3Url: 'default',
            s3Path: 'default',
            fileName: 'default',
            fileSize: 0,
            fileType: '',
            uploadTimestamp: new Date(),
            bucketType: 'approved' as const
          };
          campaignBuilderStore.updateUploadedAssets('logo', { ...defaultAsset });
          campaignBuilderStore.updateUploadedAssets('mapMarker', { ...defaultAsset });
          campaignBuilderStore.updateUploadedAssets('ar', { ...defaultAsset });
        }
        
        // Ensure modal is closed if we skipped it
        if (!showRekognitionModal.value) {
             // If we didn't show the modal, we are still loading or need to ensure we are
             // If we came from "no results" branch, loading is still active from start
        }

        isRekognizing.value = false;

        // Step 2b: Compile the MindAR target from the approved marker source.
        // Only runs when marker tracking is active AND the user supplied a
        // fresh source file this submission. Edit-mode resumes with no
        // source File fall through (the existing mindFileUrl in the store
        // is reused untouched).
        if (
          template.value.experience === 'ar' &&
          template.value.arTrackingType === 'marker' &&
          markerResizedImage
        ) {
          try {
            uiStore.startLoading('Compiling marker target...')
            campaignBuilderStore.setMarkerCompileStatus('compiling')
            campaignBuilderStore.setMarkerCompileProgress(0)
            const mindBlob = await compileMindBlob(markerResizedImage, (p) => {
              campaignBuilderStore.setMarkerCompileProgress(p)
            })
            campaignBuilderStore.setMarkerCompiled(mindBlob)

            campaignBuilderStore.setMarkerCompileStatus('uploading-mind')
            const timestamp = Date.now()
            const mindFile = new File(
              [mindBlob],
              `marker-${timestamp}.mind`,
              { type: 'application/octet-stream' },
            )
            const mindUpload = await ApiService.uploadFile(
              mindFile,
              'mind',
              `marker-mind-${timestamp}.mind`,
            )
            campaignBuilderStore.setMarkerMindFileUrl(mindUpload.s3Path)
            campaignBuilderStore.setMarkerCompileStatus('ready')
          } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to compile marker target'
            const friendly = message.includes('feature')
              ? 'Image lacks enough visual detail for tracking. Try one with more contrast and distinct features.'
              : message
            campaignBuilderStore.setMarkerCompileError(friendly)
            uiStore.stopLoading()
            showSubmitError(`Marker compile failed: ${friendly}`)
            return
          }
        }

        // Step 3: Submit form with validated assets
        const campaignData = serializeForBackend()
        
        if (isEditMode.value && editingCampaignId.value) {
          await campaignsService.recustomizeCampaign(editingCampaignId.value, campaignData as unknown as Record<string, unknown>)
          submitSuccess.value = true
          uiStore.stopLoading()
          await router.push({ name: 'Dashboard' })
          return
        }

        const response = await ApiService.submitForm(campaignData)

        const createdAt = new Date().toISOString()
        const submissionId = response.submissionId || `${LOCAL_SUBMISSION_ID_PREFIX}${createdAt}`
        const completedCampaign: CompletedCampaignSummary = {
          submissionId,
          createdAt,
          campaignName: resolveCampaignName(template.value.feature),
          featureId: template.value.feature ?? template.value.experience as any ?? null,
          scheduleStart: campaignData.campaignSchedule?.startDateTime ?? null,
          scheduleEnd: campaignData.campaignSchedule?.endDateTime ?? null,
        }
        saveCompletedCampaign(completedCampaign)

        lastSubmissionId.value = submissionId
        submitSuccess.value = true
        
        uiStore.stopLoading(); // Stop loading before showing launch modal
        showLaunchModal.value = true

      } catch (error) {
        console.error('❌ Campaign submission failed:', error)
        showSubmitError(error instanceof Error ? error.message : 'Failed to submit campaign')
        
        uiStore.stopLoading(); // Stop loading on error
        showRekognitionModal.value = false;
        // Resolve promise if it exists to prevent hanging
        if (rekognitionAcknowledgeResolver.value) {
          rekognitionAcknowledgeResolver.value();
          rekognitionAcknowledgeResolver.value = null;
        }
      } finally {
        isRekognizing.value = false;
        isSubmitting.value = false
        // Ensure loading is stopped in case we missed a path (though we shouldn't have)
        // But if we stop it here, and a modal is open, it's fine.
        // If we stop it here and we are transitioning, it's fine.
        // Actually, if we opened a modal, we already stopped it.
        // If we didn't open a modal and finished, we stopped it.
        // If we crashed, we stopped it.
        // Double stopping is fine.
        uiStore.stopLoading();
      }
    }

    const handleVerificationConfirm = async () => {
      if (!pendingVerification.username) {
        pendingVerification.error = 'No pending verification request.'
        return
      }
      if (!pendingVerification.code) {
        pendingVerification.error = 'Enter the verification code from your email.'
        return
      }

      pendingVerification.isSubmitting = true
      pendingVerification.error = ''
      try {
        await authStore.confirmSignUp(pendingVerification.username, pendingVerification.code)
      } catch (error: unknown) {
        const e = error as { message?: string; code?: string };
        // If user is already confirmed, ignore error and proceed to login
        const isAlreadyConfirmed = e?.message?.includes('CONFIRMED') || 
                                  (e?.code === 'NotAuthorizedException' && e?.message?.includes('confirmed'));
        
        if (!isAlreadyConfirmed) {
          pendingVerification.error = e?.message || 'Verification failed. Please try again.'
          pendingVerification.isSubmitting = false
          return
        }
      }

      try {
        if (!authStore.isAuthenticated) {
          await authStore.login(pendingVerification.username, pendingVerification.password)
          await authStore.checkAuth()
        }

        if (!authStore.isEmailVerified) {
          pendingVerification.error = 'Email not verified yet. Please try again.'
          return
        }

        showVerificationModal.value = false
        resolveOtpFlow(true)
      } catch (error: unknown) {
        const e = error as Error;
        console.error('Login failed after verification', e)
        pendingVerification.error = e?.message || 'Verification successful but login failed.'
      } finally {
        pendingVerification.isSubmitting = false
      }
    }

    const handleVerificationResend = async () => {
      if (!pendingVerification.username && !authStore.userDetails.email) {
        return
      }

      pendingVerification.isResending = true
      pendingVerification.error = ''
      try {
        const targetUsername = pendingVerification.username || authStore.userDetails.email
        await authStore.resendVerification(targetUsername)
      } catch (error: unknown) {
        const e = error as Error;
        pendingVerification.error = e?.message || 'Could not resend the verification code.'
      } finally {
        pendingVerification.isResending = false
      }
    }

    const handleVerificationClose = () => {
      showVerificationModal.value = false
      resolveOtpFlow(false)
    }

    const handleRekognitionAcknowledge = () => {
      const wasError = rekognitionResult.value && !rekognitionResult.value.isValid;
      
      showRekognitionModal.value = false;
      
      // Resolve the promise to continue/stop submission
      if (rekognitionAcknowledgeResolver.value) {
        rekognitionAcknowledgeResolver.value();
        rekognitionAcknowledgeResolver.value = null;
      }
      
      // If it was an error (Reupload Asset button), navigate to look-feel section
      if (wasError) {
        const lookFeelIndex = sections.value.indexOf('look-feel');
        if (lookFeelIndex !== -1) {
          navigateToSection(lookFeelIndex);
        }
      }
    }

    const handleLaunchComplete = async () => {
      showLaunchModal.value = false
      const authStore = useAuthStore()
      if (authStore.planId === 'free') {
        await router.push({ name: 'billing' })
      } else {
        await router.push({ name: 'Dashboard' })
      }
    }

    // Smooth Section Navigation
    const buildSectionsForConfig = (boundary?: CampaignBoundary | null, experience?: CampaignExperience | null) => {
      const orderedSections = [
        'splash',
        'feature-selection',
      ]

      if (boundary === 'geo') {
        orderedSections.push('location-config')
      }

      orderedSections.push('look-feel')

      if (experience === 'quiz' || experience === 'survey') {
        orderedSections.push('quiz-config')
      } else if (experience === 'ar') {
        orderedSections.push('ar-preview')
      }

      if (boundary === 'geo' || experience === 'map') {
        orderedSections.push('map-preview')
      }

      if (experience) {
        orderedSections.push('voucher-config')
      }

      orderedSections.push(
        'schedule',
        'user-details'
      )

      return orderedSections
    }

    const currentSectionId = computed(() => activeSectionId.value ?? sections.value[0] ?? 'splash')

    // Footer visibility — deliberately DUMB. The previous implementation
    // derived from the navigation store's `pendingTargetSectionId` /
    // `activeSectionId` state, which races with IntersectionObserver fires,
    // section-list rebuilds, and smooth-scroll timing: a fast Get Started
    // click could leave the store in a state where the footer's Next/Back
    // buttons stayed hidden forever after landing on the first section.
    //
    // Rule now: hide the footer iff the splash section is actually on
    // screen right now. One IntersectionObserver, one threshold, one truth
    // source. No store coupling.
    const isSplashOnScreen = ref(true)
    let splashVisibilityObserver: IntersectionObserver | null = null

    const observeSplashVisibility = () => {
      if (typeof window === 'undefined') return
      if (splashVisibilityObserver) {
        splashVisibilityObserver.disconnect()
      }
      const splashEl = document.getElementById('splash')
      if (!splashEl) return
      splashVisibilityObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return
          // Hysteresis on the 50% mark keeps the footer from flickering
          // during the smooth-scroll between splash and feature-selection:
          // once you've crossed the halfway line in either direction, the
          // state flips and stays flipped until you cross back.
          isSplashOnScreen.value = entry.intersectionRatio > 0.5
        },
        { threshold: [0, 0.25, 0.5, 0.75, 1] }
      )
      splashVisibilityObserver.observe(splashEl)
    }

    const isOnSplashTarget = computed(() => {
      // Trust the navigation store first: the moment the user clicks Get
      // Started, currentSectionId flips off 'splash' and the footer reveals
      // immediately — no waiting for the IntersectionObserver's 50% crossing
      // during the smooth-scroll. The observer is only consulted when the
      // store still says splash, as a safety net for the inverse race
      // (manual scroll drifting away while the store hasn't caught up).
      if (currentSectionId.value !== 'splash') return false
      return isSplashOnScreen.value
    })

    const MIN_SECTION_HEIGHT = 360
    const FORM_VIEW_SCROLLBAR_HIDDEN_CLASS = 'form-view-scrollbars-hidden'
    const SCROLL_ALLOWED_SELECTORS = [
      '.modal-overlay',
      '.modal-container',
      '.modal-content',
      '.modal-body',
      '[role="dialog"]',
      '.customization-modal',
      '.preview-modal-content',
      '.asset-action-modal-overlay',
      '.registration-modal-overlay',
    ] as const
    const INTERACTIVE_ELEMENT_SELECTOR =
      'input, textarea, select, button, a[href], summary, [role="button"], [tabindex]:not([tabindex="-1"])'
    const SCROLL_BLOCK_KEYS = new Set([
      'ArrowUp',
      'ArrowDown',
      'PageUp',
      'PageDown',
      'Home',
      'End',
      ' ',
      'Spacebar'
    ])

    const updateSectionViewportMetrics = () => {
      if (typeof window === 'undefined') {
        return
      }

      const root = document.documentElement

      if (uiStore.isModalOpen) {
        const isMobile = window.innerWidth < 1024
        const existingSectionHeaderHeight =
          parseFloat(getComputedStyle(root).getPropertyValue('--section-header-height')) || 0
        const existingHeaderHeight =
          document.querySelector<HTMLElement>('.app-header')?.getBoundingClientRect().height ||
          parseFloat(getComputedStyle(root).getPropertyValue('--header-height')) ||
          existingSectionHeaderHeight
        const isMobileHeaderOffsetActive = isMobile && existingSectionHeaderHeight > 0
        root.classList.toggle(
          'mobile-header-offset-active',
          isMobileHeaderOffsetActive
        )
        root.style.setProperty(
          '--mobile-header-bg',
          isMobileHeaderOffsetActive ? 'var(--vt-c-background-primary)' : 'transparent'
        )
        root.style.setProperty(
          '--mobile-header-border-color',
          isMobileHeaderOffsetActive ? 'var(--vt-c-accent)' : 'transparent'
        )
        root.style.setProperty(
          '--mobile-progress-top',
          isMobileHeaderOffsetActive ? `${existingHeaderHeight + 1}px` : '0px'
        )
        return
      }

      const visualViewport = window.visualViewport
      const viewportHeight = visualViewport?.height ?? window.innerHeight
      const viewportWidth = visualViewport?.width ?? window.innerWidth
      
      // On mobile, if we are typing, avoid updating section heights to prevent jumping
      const isTyping = formNavigationStore.scrollLocks.typing
      const isMobile = window.innerWidth < 1024

      const headerElement = document.querySelector<HTMLElement>('.app-header')
      const headerRect = headerElement?.getBoundingClientRect()
      const measuredHeaderHeight = headerRect?.height ?? 0
      const measuredHeaderBottom = headerRect?.bottom ?? 0
      const fallbackHeaderBaseHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      ) || 0
      const fallbackHeaderShellExtraHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-shell-extra-height')
      ) || 0
      const fallbackHeaderHeight = fallbackHeaderBaseHeight + fallbackHeaderShellExtraHeight
      const headerHeight = measuredHeaderHeight || fallbackHeaderHeight
      const desktopSectionStopAdjustment = isMobile ? 0 : 30
      const sectionHeaderHeight = Math.max(
        (measuredHeaderBottom || headerHeight) - desktopSectionStopAdjustment,
        headerHeight
      )

      // Always update viewport dimensions for fixed positioning
      root.style.setProperty('--section-viewport-height', `${viewportHeight}px`)
      root.style.setProperty('--section-viewport-width', `${viewportWidth}px`)
      root.style.setProperty('--section-header-height', `${sectionHeaderHeight}px`)
      const isMobileHeaderOffsetActive = isMobile && sectionHeaderHeight > 0
      root.classList.toggle(
        'mobile-header-offset-active',
        isMobileHeaderOffsetActive
      )
      root.style.setProperty(
        '--mobile-header-bg',
        isMobileHeaderOffsetActive ? 'var(--vt-c-background-primary)' : 'transparent'
      )
      root.style.setProperty(
        '--mobile-header-border-color',
        isMobileHeaderOffsetActive ? 'var(--vt-c-accent)' : 'transparent'
      )
      root.style.setProperty(
        '--mobile-progress-top',
        isMobileHeaderOffsetActive ? `${headerHeight + 1}px` : '0px'
      )

      // Only update available height if not typing on mobile to keep layout stable
      if (!isTyping || !isMobile) {
        const availableHeight = Math.max(viewportHeight - sectionHeaderHeight, MIN_SECTION_HEIGHT)
        root.style.setProperty('--section-available-height', `${availableHeight}px`)
      }

    }

    let resizeFrame = 0
    const handleViewportResize = () => {
      if (resizeFrame) {
        return
      }
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = 0
        updateSectionViewportMetrics()
      })
    }

    // Watch for boundary/experience selection to toggle dynamic sections
    watch(
      () => [template.value.boundary, template.value.experience] as const,
      ([boundary, experience]) => {
        formNavigationStore.setSections(buildSectionsForConfig(boundary, experience))
      },
      { immediate: true }
    )

    let sectionTransitionFrame = 0

    watch(
      activeSectionId,
      (sectionId) => {
        uiStore.setCurrentSection(sectionId ?? '')
      },
      { immediate: true }
    )

    const clearSectionTransitionQueue = () => {
      if (sectionTransitionFrame) {
        cancelAnimationFrame(sectionTransitionFrame)
        sectionTransitionFrame = 0
      }
    }

    const clampSectionIndex = (index: number) => {
      const maxIndex = sections.value.length - 1
      if (maxIndex < 0) return 0
      return Math.min(Math.max(index, 0), maxIndex)
    }

    const isSectionAnchoredInViewport = (sectionId: string): boolean => {
      const element = document.getElementById(sectionId)
      if (!element) {
        return false
      }

      const viewportHeight = window.visualViewport?.height ?? window.innerHeight
      const topThreshold = Math.max(48, viewportHeight * 0.18)
      const { top } = element.getBoundingClientRect()

      return top >= -topThreshold && top <= topThreshold
    }

    const settleSectionTransition = (sectionId: string) => {
      if (typeof window === 'undefined') {
        formNavigationStore.completeSectionTransition(sectionId)
        return
      }

      clearSectionTransitionQueue()

      let attempts = 0
      const maxAttempts = 24

      const settle = () => {
        if (pendingTargetSectionId.value !== sectionId) {
          formNavigationStore.completeSectionTransition(sectionId)
          return
        }

        if (
          visibleSectionId.value === sectionId ||
          isSectionAnchoredInViewport(sectionId) ||
          attempts >= maxAttempts
        ) {
          formNavigationStore.completeSectionTransition(sectionId)
          return
        }

        attempts += 1
        sectionTransitionFrame = window.requestAnimationFrame(settle)
      }

      sectionTransitionFrame = window.requestAnimationFrame(settle)
    }

    const getSectionElement = async (sectionId: string) => {
      let element = document.getElementById(sectionId)

      if (element) {
        return element
      }

      await nextTick()
      element = document.getElementById(sectionId)

      return element
    }

    const navigateToSection = async (
      index: number,
      options: {
        behavior?: ScrollBehavior
        reason?: 'programmatic-scroll' | 'restoring-from-modal'
      } = {}
    ) => {
      const safeIndex = clampSectionIndex(index)
      const sectionId = sections.value[safeIndex]
      const element = await getSectionElement(sectionId)
      if (!element) return false

      const behavior = options.behavior ?? 'smooth'
      const reason = options.reason ?? 'programmatic-scroll'

      if (!formNavigationStore.beginSectionTransition(sectionId, reason)) {
        return false
      }

      element.scrollIntoView({
        behavior,
        block: 'start'
      })

      settleSectionTransition(sectionId)
      return true
    }

    const restoreSectionAfterModalClose = () => {
      if (typeof window === 'undefined') {
        return
      }
      const targetSectionId = formNavigationStore.prepareModalRestore()
      if (!targetSectionId) {
        return
      }

      document.documentElement.classList.remove('is-typing')
      formNavigationStore.setScrollLock('typing', false)

      nextTick(() => {
        updateSectionViewportMetrics()
        const targetIndex = sections.value.indexOf(targetSectionId)
        if (targetIndex === -1) {
          formNavigationStore.completeSectionTransition(targetSectionId)
          return
        }
        navigateToSection(targetIndex, {
          behavior: 'auto',
          reason: 'restoring-from-modal',
        })
      })
    }

    watch(
      () => uiStore.isModalOpen,
      (isOpen, wasOpen) => {
        if (isOpen && !wasOpen) {
          formNavigationStore.rememberModalOrigin(currentSectionId.value)
          return
        }
        if (!isOpen && wasOpen) {
          restoreSectionAfterModalClose()
          return
        }
        if (!isOpen) {
          formNavigationStore.clearModalState()
        }
      }
    )

    const restoreBillingReturnSection = (): boolean => {
      if (sessionStorage.getItem(BILLING_RESUME_PENDING_KEY) !== '1') {
        return false
      }

      const savedSectionId =
        sessionStorage.getItem(BILLING_RESUME_SECTION_KEY) || BILLING_RESUME_FALLBACK_SECTION
      let targetIndex = sections.value.indexOf(savedSectionId)

      if (targetIndex === -1) {
        targetIndex = sections.value.indexOf(BILLING_RESUME_FALLBACK_SECTION)
      }

      sessionStorage.removeItem(BILLING_RESUME_PENDING_KEY)
      sessionStorage.removeItem(BILLING_RESUME_SECTION_KEY)

      if (targetIndex === -1) {
        return false
      }

      nextTick(() => {
        navigateToSection(targetIndex, { behavior: 'auto' })
      })

      return true
    }

    const navigateToNextSection = async () => {
      const nextSectionId = formNavigationStore.nextSectionId
      if (!nextSectionId) {
        return
      }

      const nextIndex = sections.value.indexOf(nextSectionId)
      if (nextIndex !== -1) {
        await navigateToSection(nextIndex)
      }
    }

    const handleShowHowItWorksFromSplash = () => {
      uiStore.setCurrentSection('splash')
      emit('show-how-it-works')
    }

    const handleShowCaseStudies = () => {
      emit('show-case-studies')
    }

    const handleShowHowItWorksFromFooter = () => {
      uiStore.setCurrentSection(currentSectionId.value)
      emit('show-how-it-works')
    }

    const navigateToPrevSection = async () => {
      const previousSectionId = formNavigationStore.previousSectionId
      if (!previousSectionId) {
        return
      }

      const previousIndex = sections.value.indexOf(previousSectionId)
      if (previousIndex !== -1) {
        await navigateToSection(previousIndex)
      }
    }

    // Enhanced scrollTo function for navigation links
    const scrollTo = async (selector: string) => {
      const sectionId = selector.replace('#', '')
      const index = sections.value.indexOf(sectionId)
      if (index !== -1) {
        await navigateToSection(index)
      } else {
        // Fallback for non-section elements
        const el = document.querySelector(selector)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }
    }

    // Block all scroll intent in the form view
    const handleWheel = (event: WheelEvent) => {
      if (isScrollAllowedTarget(event.target)) {
        return
      }
      event.preventDefault()
    }

    const handleKeydown = (event: KeyboardEvent) => {
      if (!SCROLL_BLOCK_KEYS.has(event.key)) {
        return
      }
      if (isScrollAllowedTarget(event.target) || isInteractiveTarget(event.target)) {
        return
      }
      event.preventDefault()
    }

    // Handle focus to avoid layout jumps while typing
    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || 
                     target.tagName === 'TEXTAREA' || 
                     target.isContentEditable ||
                     target.closest('.search-input') ||
                     target.closest('.form-field');
      
      if (isInput) {
        document.documentElement.classList.add('is-typing');
        formNavigationStore.setScrollLock('typing', true)
      }
    };

    const handleFocusOut = (event: FocusEvent) => {
      const relatedTarget = event.relatedTarget as HTMLElement;
      // Only remove if we're not moving to another input
      if (!relatedTarget || 
          !(relatedTarget.tagName === 'INPUT' || 
            relatedTarget.tagName === 'TEXTAREA' || 
            relatedTarget.isContentEditable ||
            relatedTarget.closest('.search-input') ||
            relatedTarget.closest('.form-field'))) {
        document.documentElement.classList.remove('is-typing');
        formNavigationStore.setScrollLock('typing', false)
      }
    };

    // Catch keyboard intent even earlier on touch devices
    const handleTouchStart = (event: TouchEvent) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        document.documentElement.classList.add('is-typing');
        formNavigationStore.setScrollLock('typing', true)
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (isScrollAllowedTarget(event.target)) {
        return
      }
      event.preventDefault()
    }

    // Track sections with IntersectionObserver
    const observer = ref<IntersectionObserver | null>(null);

    const setupObserver = () => {
      if (observer.value) {
        observer.value.disconnect();
      }

      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.5
      };

      observer.value = new IntersectionObserver((entries) => {
        if (!formNavigationStore.canTrackViewport) {
          return;
        }
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.id;
            formNavigationStore.reportVisibleSection(sectionId);
          }
        });
      }, options);

      // Observe all section elements
      nextTick(() => {
        sections.value.forEach(sectionId => {
          const el = document.getElementById(sectionId);
          if (el) observer.value?.observe(el);
        });
      });
    }

    const setFormViewScrollbarVisibility = (isHidden: boolean) => {
      if (typeof document === 'undefined') {
        return
      }

      document.documentElement.classList.toggle(FORM_VIEW_SCROLLBAR_HIDDEN_CLASS, isHidden)
      document.body.classList.toggle(FORM_VIEW_SCROLLBAR_HIDDEN_CLASS, isHidden)
    }

    let layoutReadyCleanup: (() => void) | null = null
    // Guards against an async-race where onBeforeUnmount fires (and runs
    // cleanup) BEFORE the async initialize() finishes attaching its
    // window-level listeners. Without this, leaked listeners block scroll
    // on every other view (Experience Library, Dashboard, etc.) until a
    // full reload. Memory: feedback_home_only_wheel_block.md.
    let isMounted = true

    // Lifecycle hooks
    onMounted(() => {
      // Hard-top on every mount. Per-section deep linking can override later.
      window.scrollTo(0, 0)
      setFormViewScrollbarVisibility(true)

      // Start observing splash visibility as soon as the DOM is painted.
      // nextTick pushes the observer wiring past the current microtask so
      // the SplashSection child component has had a chance to render its
      // `#splash` element.
      nextTick(() => observeSplashVisibility())

      uiStore.startLoading()

      const clearLayoutLoader = () => {
        if (layoutReadyCleanup) {
          layoutReadyCleanup()
          layoutReadyCleanup = null
        }
        uiStore.stopLoading()
      }

      const onLayoutReady = () => {
        clearLayoutLoader()
      }

      // Splash is static — layout is ready as soon as the first paint lands.
      // Don't gate the spinner on network-bound data fetches in initialize().
      const failsafeTimer = window.setTimeout(() => {
        console.warn('[FormView] app-layout-ready TTL elapsed; clearing loader')
        clearLayoutLoader()
      }, 1500)

      window.addEventListener('app-layout-ready', onLayoutReady, { once: true })

      layoutReadyCleanup = () => {
        window.removeEventListener('app-layout-ready', onLayoutReady)
        window.clearTimeout(failsafeTimer)
      }

      // Dispatch layout-ready on a short timer so the spinner is actually
      // visible briefly — especially important when the user navigates in
      // from the dashboard/billing, where the bundle is cached and the
      // page would otherwise render instantly with no loading feedback.
      // Splash itself is static so there's no real work to gate on.
      const MIN_LOADER_MS = 400
      setTimeout(() => {
        updateSectionViewportMetrics()
        window.dispatchEvent(new Event('app-layout-ready'))
      }, MIN_LOADER_MS)

      // Fire and forget — the registration modal uses this list to build
      // a unique default campaign name. Missing list just falls through
      // to the bare default.
      void refreshExistingCampaignNames()

      const initialize = async () => {
        await experiencesService.syncLibrary(true)
        if (isEditMode.value) {
          await loadCampaignForEdit()
        } else {
          selectedExperienceDraftKey.value = ''
          experienceAttachMode.value = 'quick'
          loadState()
          if (typeof route.query.experienceDraft === 'string') {
            const record = getExperienceLibraryRecord(route.query.experienceDraft)
              || await experiencesService.getExperience(route.query.experienceDraft)
            if (record) {
              const experience = (record.quizConfig.experienceMode === 'survey' ? 'survey' : 'quiz') as CampaignExperience
              campaignBuilderStore.setExperience(experience)
              campaignBuilderStore.setExperienceType(experience)
              const hydratedQuizConfig = materializeExperienceIdentity(normalizeQuizConfig(record.quizConfig))
              campaignBuilderStore.setQuizConfig(hydratedQuizConfig)
              saveExperienceLibraryRecord({
                quizConfig: hydratedQuizConfig,
                campaignName: template.value.campaignName || record.name,
                ...orgStamp(),
              })
              if (!template.value.campaignName && record.name) {
                template.value.campaignName = record.name
              }
              if (record.status === 'published') {
                selectedExperienceDraftKey.value = record.localKey
                experienceAttachMode.value = 'library'
                template.value.attachedExperience = buildAttachedExperienceReference(record)
              } else {
                selectedExperienceDraftKey.value = ''
                experienceAttachMode.value = 'quick'
                template.value.attachedExperience = null
              }
            }
          }
        }

        if ((template.value.experience === 'quiz' || template.value.experience === 'survey') && template.value.quizConfig) {
          const hydratedQuizConfig = materializeExperienceIdentity(normalizeQuizConfig(template.value.quizConfig))
          campaignBuilderStore.setQuizConfig(hydratedQuizConfig)
          saveExperienceLibraryRecord({
            quizConfig: hydratedQuizConfig,
            campaignName: template.value.campaignName || undefined,
            ...orgStamp(),
          })
        }

        if (!selectedExperienceDraftKey.value) {
          experienceAttachMode.value = 'quick'
          template.value.attachedExperience = null
        }
        campaignBuilderStore.updateSectionCompletion()
        updateSectionViewportMetrics()
        nextTick(() => {
          updateSectionViewportMetrics()
          window.dispatchEvent(new Event('app-layout-ready'))
        })

        if (!isMounted) return
        window.addEventListener('resize', handleViewportResize)
        window.addEventListener('orientationchange', handleViewportResize)
        window.visualViewport?.addEventListener('resize', handleViewportResize)

        const toDateValue = (date: Date): string => {
          const year = date.getFullYear()
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          return `${year}-${month}-${day}`
        }

        const toTimeValue = (date: Date): string => {
          const hours = String(date.getHours()).padStart(2, '0')
          const minutes = String(date.getMinutes()).padStart(2, '0')
          return `${hours}:${minutes}`
        }

        const toValidDate = (value: unknown): Date | null => {
          if (!value) return null
          const parsed = value instanceof Date ? new Date(value) : new Date(String(value))
          return Number.isNaN(parsed.getTime()) ? null : parsed
        }

        const scheduleStart = toValidDate(campaignBuilderStore.schedule.startDateTime)
        const scheduleEnd = toValidDate(campaignBuilderStore.schedule.endDateTime)

        if (!scheduleStart || !scheduleEnd) {
          const startDate = scheduleStart ?? new Date()
          const endDate = scheduleEnd ?? new Date(startDate)

          if (!scheduleEnd) {
            endDate.setDate(endDate.getDate() + 28)
          }

          campaignBuilderStore.schedule.startTime = toTimeValue(startDate)
          campaignBuilderStore.schedule.startDate = toDateValue(startDate)
          campaignBuilderStore.schedule.startDateTime = startDate
          campaignBuilderStore.schedule.endTime = toTimeValue(endDate)
          campaignBuilderStore.schedule.endDate = toDateValue(endDate)
          campaignBuilderStore.schedule.endDateTime = endDate
        }

        // Add listeners for strict section navigation. Bail if unmounted —
        // otherwise these leak to other views and block their native scroll.
        if (!isMounted) return
        window.addEventListener('wheel', handleWheel, { passive: false })
        window.addEventListener('touchstart', handleTouchStart, { passive: true })
        window.addEventListener('touchmove', handleTouchMove, { passive: false })
        window.addEventListener('keydown', handleKeydown)
        window.addEventListener('focusin', handleFocusIn)
        window.addEventListener('focusout', handleFocusOut)

        // Initialize IntersectionObserver
        setupObserver();
      
        // Always settle to the first section (splash). Per-section deep linking
        // will be added later; stability first.
        const hasRestoredBillingSection = !isEditMode.value && restoreBillingReturnSection()
        if (!hasRestoredBillingSection) {
          formNavigationStore.setSettledSection(sections.value[0] ?? null)
        }
      }
      void initialize().catch((error) => {
        console.error('Failed to initialize form view', error)
        clearLayoutLoader()
        uiStore.showError({
          title: 'Unable To Load Campaign',
          message: error instanceof Error ? error.message : 'Failed to load the campaign editor.',
        })
      })
    })

    watch(
      () => [template.value.boundary, template.value.experience],
      () => {
        updateTotalSteps()
      },
      { immediate: true }
    )

    // Watch for sections changing to re-initialize observer
    watch(sections, () => {
      setupObserver();
    }, { deep: true });

    const isPhoneInputComplete = (countryCode: string, localNumber: string) => {
      const codeDigits = (countryCode ?? '').replace(/[^\d]/g, '')
      const normalizedNumber = (localNumber ?? '').replace(/\D/g, '').replace(/^0+/, '')
      return codeDigits.length > 0 && normalizedNumber.length >= 7
    }

    const normalizePassword = (pw?: string) => (pw ?? '').trim()

    const isPasswordValid = (password: string) => {
      const pw = normalizePassword(password)
      if (!pw || pw.length < 8) return false
      const hasUpper = /[A-Z]/.test(pw)
      const hasLower = /[a-z]/.test(pw)
      const hasNumber = /[0-9]/.test(pw)
      return hasUpper && hasLower && hasNumber
    }

    // Sync authenticated user details to form
    watch(
      [
        () => authStore.isAuthenticated,
        () => authStore.userDetails,
        () => userDetails.value.email,
        () => userDetails.value.password,
        () => userDetails.value.confirmPassword
      ],
      ([isAuthed, authDetails]) => {
        
        if (isAuthed && authDetails) {
          campaignBuilderStore.syncUserDetails(authDetails);
        }
        
        // Update account-details step completion
        const hasEmail = !!(userDetails.value.email && /.+@.+\..+/.test(userDetails.value.email));
        const passwordRequired = !isAuthed;
        const password = normalizePassword(userDetails.value.password);
        const confirm = normalizePassword(userDetails.value.confirmPassword);
        const isPasswordOk = passwordRequired ? isPasswordValid(password) : true;
        const isConfirmOk = passwordRequired ? (password === confirm && confirm.length > 0) : true;
        
        const isAccountComplete = isAuthed || (hasEmail && isPasswordOk && isConfirmOk);

        if (isAccountComplete) {
          uiStore.completeStep('account-details');
        } else {
          uiStore.removeStep('account-details');
        }
      },
      { immediate: true, deep: true }
    );

    watch([() => userDetails.value, () => authStore.isAuthenticated], ([details]) => {
      const isNameValid = details.name.trim().length >= 3;
      const isSurnameValid = details.surname.trim().length >= 3;
      const isMobileValid = isPhoneInputComplete(details.mobileCountryCode, details.mobileNumber);
      const isConsentValid = details.consent;

      if (isNameValid && isSurnameValid && isMobileValid && isConsentValid) {
        uiStore.completeStep('profile-details');
      } else {
        uiStore.removeStep('profile-details');
      }
    }, { deep: true });

    // Cleanup event listeners
    const cleanup = () => {
      if (resizeFrame) {
        cancelAnimationFrame(resizeFrame);
        resizeFrame = 0;
      }
      clearSectionTransitionQueue()
      window.removeEventListener('resize', handleViewportResize);
      window.removeEventListener('orientationchange', handleViewportResize);
      window.visualViewport?.removeEventListener('resize', handleViewportResize);
      window.removeEventListener('wheel', handleWheel)
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('keydown', handleKeydown)
      window.removeEventListener('focusin', handleFocusIn)
      window.removeEventListener('focusout', handleFocusOut)
      document.documentElement.classList.remove('is-typing')
      document.documentElement.classList.remove('mobile-header-offset-active')
      document.documentElement.style.removeProperty('--mobile-header-bg')
      document.documentElement.style.removeProperty('--mobile-header-border-color')
      document.documentElement.style.removeProperty('--mobile-progress-top')
      setFormViewScrollbarVisibility(false)
      if (observer.value) {
        observer.value.disconnect();
      }
      if (layoutReadyCleanup) {
        layoutReadyCleanup()
        layoutReadyCleanup = null
      }
      uiStore.stopLoading()
      formNavigationStore.$reset()
    }

    onBeforeUnmount(() => {
      // Flip BEFORE cleanup so any in-flight initialize() bails out of its
      // listener-attach step rather than racing past cleanup. See the
      // isMounted declaration comment above.
      isMounted = false
      cleanup()
      if (splashVisibilityObserver) {
        splashVisibilityObserver.disconnect()
        splashVisibilityObserver = null
      }
    })

    const features = computed(() => FEATURE_OPTIONS);
    const boundaryOptions = computed(() => BOUNDARY_OPTIONS);
    const experienceOptions = computed(() => EXPERIENCE_OPTIONS);

    // Watch for completion changes
    watch(campaignBuilderStore, campaignBuilderStore.updateSectionCompletion, { deep: true })


    const handleBoundarySelect = (boundaryId: string) => {
      campaignBuilderStore.setBoundary(boundaryId as CampaignBoundary);
      formNavigationStore.setSections(buildSectionsForConfig(boundaryId as CampaignBoundary, template.value.experience));
      uiStore.completeStep('feature-selection');
    };

    const handleExperienceSelect = (experienceId: CampaignExperience) => {
      campaignBuilderStore.setExperience(experienceId);
      campaignBuilderStore.setExperienceType(experienceId);
      formNavigationStore.setSections(buildSectionsForConfig(template.value.boundary, experienceId));

      if (experienceId === 'ar') {
        campaignBuilderStore.lookFeel.stagedAssets.mapMarker = undefined;
        campaignBuilderStore.lookFeel.selectedDefaultAssets.mapMarker = null;
        campaignBuilderStore.lookFeel.uploadedAssets.mapMarker = undefined;
        uiStore.removeStep('map-marker-asset');
      } else if (experienceId === 'quiz' || experienceId === 'survey') {
        campaignBuilderStore.lookFeel.stagedAssets.mapMarker = undefined;
        campaignBuilderStore.lookFeel.selectedDefaultAssets.mapMarker = null;
        campaignBuilderStore.lookFeel.uploadedAssets.mapMarker = undefined;
        campaignBuilderStore.lookFeel.stagedAssets.glbModel = undefined;
        campaignBuilderStore.lookFeel.selectedDefaultAssets.ar = null;
        campaignBuilderStore.lookFeel.uploadedAssets.glbModel = undefined;
        uiStore.removeStep('map-marker-asset');
        uiStore.removeStep('ar-asset');
      }

      uiStore.completeStep('feature-selection');
    };

    // Legacy handler kept for backward compat
    const handleFeatureSelect = async (featureId: CampaignFeature) => {
      campaignBuilderStore.setFeature(featureId);
    };

    // Helper to convert hex to RGBA
    const hexToRgba = (hex: string, alpha: number = 1): string => {
      let r = 0, g = 0, b = 0;

      // Handle #RRGGBB or #RGB
      if (hex.startsWith('#')) {
        hex = hex.slice(1);
      }

      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else if (hex.length === 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      } else {
        console.warn('Invalid hex colour format:', hex);
        return `rgba(0, 0, 0, ${alpha})`; // Default to black if invalid
      }

      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const handleFileRemove = (type: 'logo' | 'mapMarker' | 'ar' | 'background') => {
      clearSelectedAsset(type, activeBackgroundScreen.value);
    };

    const isSectionComplete = (sectionId: string): boolean => {
      const completed = uiStore.completedSteps;
      switch (sectionId) {
        case 'splash':
        case 'preview-experience':
          return true;
        case 'feature-selection':
          return completed.has('feature-selection');
        case 'ar-preview': {
          const assetComplete = completed.has('ar-asset');
          // IRL2-2900: in marker mode, NEXT also requires a marker tracer
          // image (the target users point their camera at), on top of the
          // 3D asset.
          if (template.value.arTrackingType === 'marker') {
            const hasMarkerImage = Boolean(
              campaignBuilderStore.markerTarget.sourceImagePreview ||
              campaignBuilderStore.markerTarget.sourceImageUrl
            );
            return assetComplete && hasMarkerImage;
          }
          return assetComplete;
        }
        case 'quiz-config':
          return completed.has('quiz-config');
        case 'map-preview':
          return !isMapMarkerEnabled.value || completed.has('map-marker-asset');
        case 'location-config':
          return completed.has('location');
        case 'voucher-config':
          if (template.value.experience === 'quiz' || template.value.experience === 'survey') {
            return completed.has('message');
          }
          return completed.has('voucher-config') && completed.has('message');
        case 'look-feel':
          return completed.has('logo-asset');
        case 'message':
          return completed.has('message');
        case 'schedule':
          return completed.has('campaign-schedule');
        case 'user-details':
          return completed.has('account-details') && completed.has('profile-details');
        case 'company-details':
          return completed.has('company-details');
        default:
          return true; // Non-blocking by default
      }
    };

    const isCurrentSectionComplete = computed(() => {
      return isSectionComplete(currentSectionId.value);
    });

    const isNavigationBusy = computed(() => {
      return isTransitioning.value || uiStore.isModalOpen || uiStore.isLoading || isSubmitting.value;
    });

    const isBackButtonDisabled = computed(() => {
      return isFirstSection.value || isNavigationBusy.value;
    });

    const isContinueButtonDisabled = computed(() => {
      if (isNavigationBusy.value) return true;
      if (currentSectionId.value === 'feature-selection') {
        return !template.value.boundary || !template.value.experience;
      }
      return false;
    });

    const getMissingFieldsForSection = (sectionId: string): string[] => {
      const completed = uiStore.completedSteps;
      const missing: string[] = [];

      switch (sectionId) {
        case 'feature-selection':
          if (!completed.has('feature-selection')) missing.push('Campaign Feature Selection');
          break;
        case 'ar-preview':
          if (!completed.has('ar-asset')) missing.push('AR Experience Asset (.glb)');
          // IRL2-2900: marker mode also needs a marker tracer image.
          if (
            template.value.arTrackingType === 'marker' &&
            !campaignBuilderStore.markerTarget.sourceImagePreview &&
            !campaignBuilderStore.markerTarget.sourceImageUrl
          ) {
            missing.push('Marker Tracer Image');
          }
          break;
        case 'quiz-config':
          if (!completed.has('quiz-config')) missing.push('Quiz Questions (at least one with correct answer marked)');
          break;
        case 'map-preview':
          if (isMapMarkerEnabled.value && !completed.has('map-marker-asset')) missing.push('Map Marker Icon');
          break;
        case 'location-config':
          if (!completed.has('location')) missing.push('Location Configuration on Map');
          break;
        case 'voucher-config':
          if (template.value.experience !== 'quiz' && template.value.experience !== 'survey' && !completed.has('voucher-config')) {
            missing.push('Voucher Setup (Generate or Upload CSV)');
          }
          if (!completed.has('message')) missing.push('Prize Message');
          break;
        case 'look-feel':
          if (!completed.has('logo-asset')) missing.push('Brand Logo');
          break;
        case 'schedule':
          if (!completed.has('campaign-schedule')) missing.push('Campaign Start/End Dates & Times');
          break;
        case 'user-details':
          if (!completed.has('account-details')) {
            if (!userDetails.value.email) missing.push('Account Email');
            if (!authStore.isAuthenticated && !userDetails.value.password) missing.push('Account Password');
            if (!authStore.isAuthenticated && userDetails.value.password !== userDetails.value.confirmPassword) missing.push('Passwords must match');
          }
          if (!completed.has('profile-details')) {
            if (!userDetails.value.name) missing.push('First Name');
            if (!userDetails.value.surname) missing.push('Surname');
            if (!isPhoneInputComplete(userDetails.value.mobileCountryCode, userDetails.value.mobileNumber)) missing.push('Valid Mobile Number');
            if (!userDetails.value.consent) missing.push('Terms & Conditions Consent');
          }
          break;
      }
      return missing;
    };

    const handleGetStarted = async () => {
      const featureIndex = sections.value.indexOf('feature-selection')
      if (featureIndex !== -1) {
        formNavigationStore.setSettledSection('feature-selection')
        await navigateToSection(featureIndex, { behavior: 'smooth' })
      }
    }

    const handleContinueClick = () => {
      if (isNavigationBusy.value) return;

      if (isLastSection.value) {
        submitCampaign();
      } else {
        navigateToNextSection();
      }
    };

    /**
     * Called only when the registration details modal closes.
     *
     * The modal-close uiStore watcher already kicks off
     * restoreSectionAfterModalClose which locks-and-scrolls the section
     * back to its top anchor. We wait for that transition to settle, then
     * re-enter the transition machinery with block: 'end' so the page
     * finishes on the BOTTOM of the user-details section (where the footer
     * nav + launch button live). Going through beginSectionTransition
     * means iOS Safari's scroll-lock observers don't fight us — same path
     * the Next button uses.
     */
    const scrollUserDetailsToBottom = () => {
      const sectionId = 'user-details'
      const tryScroll = (attempt = 0) => {
        if (formNavigationStore.isTransitioning && attempt < 20) {
          setTimeout(() => tryScroll(attempt + 1), 50)
          return
        }
        getSectionElement(sectionId).then((element) => {
          if (!element) return
          if (!formNavigationStore.beginSectionTransition(sectionId, 'programmatic-scroll')) {
            return
          }
          element.scrollIntoView({ behavior: 'smooth', block: 'end' })
          settleSectionTransition(sectionId)
        })
      }
      // Defer a frame so the modal teardown is in the DOM before we measure.
      requestAnimationFrame(() => tryScroll())
    }

    const currentMissingFields = computed(() => {
      return getMissingFieldsForSection(currentSectionId.value);
    });

    const campaignBuilderState = campaignBuilderStore

    return {
      // Store
      authStore,
      uiStore,
      campaignBuilderStore,
      campaignBuilderState,
      
      // State
      template,
      lookFeel,
      drops,
      message,
      schedule,
      userDetails,
      validation,
      completion,
      templateConfig,
      isMapMarkerEnabled,
      screenBrandColors,
      registrationBrandColors,
      interactionBrandColors,
      prizeBrandColors,
      uploadedAssets,
      dropConfig,
      prizeMessage,
      campaignSchedule,
      scheduleValidation,
      experienceAttachMode,
      hasChosenExperienceAttachMode,
      setExperienceAttachMode,
      availableExperienceOptions,
      initialVisibleExperienceCount,
      publishedExperienceOptions,
      selectedAttachedExperience,
      selectedAttachedLatestPublishedExperience,
      selectedExperienceDraftKey,
      placeholderAssets,
      features,
      boundaryOptions,
      experienceOptions,
      defaultLogos,
      defaultMapMarkers,
      defaultBackgrounds,
      defaultArAssets,
      splashModelUrl,

      // Previews
      logoPreviewUrl,
      mapMarkerPreviewUrl,
      registrationBackgroundPreviewUrl,
      prizeBackgroundPreviewUrl,
      selectedBackgroundAssetId,
      arAssetPreviewInfo,
      registrationButtonConfig,
      interactionButtonConfig,
      prizeButtonConfig,
      registrationButtonStyle,
      interactionButtonStyle,
      prizeButtonStyle,
      defaultLogo,

      // Modals
      showHoldingModal,
      showGlbInfoModal,
      showLaunchModal,
      showRekognitionModal,
      showSavedExperiencesModal,
      openSavedExperiencesModal,
      closeSavedExperiencesModal,
      handleSavedExperienceSelect,
      showVerificationModal,
      pendingVerification,
      rekognitionResult,
      isAssetModalVisible,
      isUploadModalVisible,
      assetModalAllowedTabs,
      assetModalInitialTab,
      uploadModalAllowedTypes,
      // Navigation
      scrollTo,
      sections,
      currentSectionIndex,
      isFirstSection,
      isOnSplashTarget,
      isLastSection,
      isNavigationBusy,
      isBackButtonDisabled,
      isContinueButtonDisabled,
      navigateToSection,
      navigateToNextSection,
      navigateToPrevSection,
      getSectionCompletion,
      currentMissingFields,

      // Quiz
      quizConfig,
      handleQuizConfigUpdate,
      handleExperienceDraftSelect,
      selectAttachedExperience,
      clearAttachedExperience,

      // Handlers
      handleTemplateSelection,
      handleBoundarySelect,
      handleExperienceSelect,
      handleFeatureSelect,
      handleUploadComplete,
      handleDefaultAssetSelect,
      handleAssetsUpdate,
      handleBrandColorChange,
      handleGoogleFontChange,
      handleOpenAssetModal,
      handleOpenLogoCustomizer,
      handleOpenUploadModal,
      handleMessageUpdate,
      handleVoucherConfigUpdate,
      handleCtaConfigUpdate,
      handleScreenButtonConfigUpdate,
      handleVerificationConfirm,
      handleVerificationResend,
      handleVerificationClose,
      updateUserDetails,
      validateDropConfig,
      handleScheduleChange,
      clearSelectedAsset,
      handleRekognitionAcknowledge,
      handleGetStarted,
      handleContinueClick,
      scrollUserDetailsToBottom,
      existingCampaignNames,
      handleShowHowItWorksFromSplash,
      handleShowHowItWorksFromFooter,
      handleShowCaseStudies,

      // Formatters
      getRatioDescription,
      formatTimePeriod,
      updateMessageCount,
      isNearLimit,
      isAtLimit,
      formatScheduledTime,
      formatScheduledEndTime,
      getTimeUntilLaunch,
      getCurrentTimezone,

      // State Management
      serializeForBackend,
      isFormComplete,
      getCompletionPercentage,
      clearState,

      // Campaign Submission
      isSubmitting,
      submitSuccess,
      submitCampaign,
      lastSubmissionId,
      isRekognizing,
      handleLaunchComplete,

      // Cleanup
      cleanup,
      handleFileRemove,
      isCurrentSectionComplete,
    }
  },
})
</script>

<style scoped>
.form-view {
  padding-top: var(--section-header-height);
  position: relative;
  z-index: 1;
}

:global(html.form-view-scrollbars-hidden),
:global(body.form-view-scrollbars-hidden) {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

:global(html.form-view-scrollbars-hidden::-webkit-scrollbar),
:global(body.form-view-scrollbars-hidden::-webkit-scrollbar),
.form-view :deep(::-webkit-scrollbar) {
  width: 0;
  height: 0;
  display: none;
}

.form-view :deep(*) {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

/* Steps */
.steps-content {
  width: 100%;
  max-width: var(--base-layout-section-max-width);
  margin-inline: auto;
}

.steps-header {
  text-align: center;
  margin-bottom: clamp(32px, 5vw, 70px);
}

.steps-header h3 {
  font-size: clamp(1.5rem, 1.9vw, 2.4rem);
  font-weight: 700;
  color: var(--color-heading);
  margin-bottom: 8px;
}

.steps-header p {
  color: var(--color-text);
  opacity: 0.9;
  font-size: clamp(0.95rem, 1.2vw, 1.2rem);
}

.steps-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(24px, 4vw, 72px);
  align-items: center;
}

.video-container {
  width: 100%;
}

.demo-video {
  width: 100%;
  aspect-ratio: 16/9;
  border-radius: 20px;
  background: linear-gradient(135deg, rgba(var(--vt-c-accent-rgb), 0.15), rgba(41, 46, 80, 0.9));
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(var(--vt-c-accent-rgb), 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
}

.demo-video:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(var(--vt-c-accent-rgb), 0.2);
}

.play-button {
  font-size: 4rem;
  margin-bottom: 10px;
  opacity: 0.9;
  transition: all 0.3s ease;
}

.demo-video:hover .play-button {
  transform: scale(1.1);
  opacity: 1;
}

.video-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-text);
  opacity: 0.9;
}

.steps-list-container {
  width: 100%;
}

.steps-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: clamp(16px, 2.2vw, 28px);
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: clamp(12px, 1.6vw, 28px);
  padding: clamp(14px, 2.2vw, 32px);
  background-color: var(--color-background-soft);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  transition: all 0.3s ease;
}

.step-item:hover {
  border-color: var(--vt-c-accent);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(var(--vt-c-accent-rgb), 0.1);
  transform: translateY(-2px);
}

.step-number {
  flex-shrink: 0;
  width: clamp(30px, 2.2vw, 52px);
  height: clamp(30px, 2.2vw, 52px);
  background: var(--primary-gradient);
  color: var(--vt-c-background-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: clamp(1rem, 1.4vw, 1.2rem);
  box-shadow: 0 4px 15px rgba(var(--vt-c-accent-rgb), 0.3);
}

.step-content h4 {
  margin: 0 0 8px 0;
  font-size: clamp(1rem, 1.3vw, 1.25rem);
  font-weight: 600;
  color: var(--color-heading);
}

.step-content p {
  margin: 0;
  color: var(--color-text);
  opacity: 0.9;
  line-height: clamp(1.35, 1.3vw, 1.55);
}

/* Template */
.template-content {
  width: 100%;
  max-width: var(--base-layout-section-max-width);
  margin-inline: auto;
}

/* Drops Configuration */
.drops-content {
  width: 100%;
  max-width: var(--base-layout-schedule-form-max-width);
  margin-inline: auto;
}

.drops-form {
  background-color: var(--color-background-soft);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  padding: clamp(20px, 2.8vw, 48px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(var(--vt-c-accent-rgb), 0.05);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: clamp(18px, 2.2vw, 32px);
  margin-bottom: clamp(20px, 3vw, 40px);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 600;
  color: var(--color-heading);
  font-size: clamp(0.9rem, 1.1vw, 1.05rem);
}

.form-group input,
.form-group select {
  padding: clamp(10px, 1.05vw, 16px) clamp(12px, 1.4vw, 20px);
  border: 2px solid var(--color-border);
  border-radius: 12px;
  background-color: var(--color-background-mute);
  color: var(--color-text);
  font-size: clamp(0.95rem, 1.1vw, 1.1rem);
  transition: all 0.3s ease;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: var(--vt-c-accent);
  box-shadow: 0 0 0 3px rgba(var(--vt-c-accent-rgb), 0.2);
  background-color: var(--color-background-secondary);
}

.field-description {
  font-size: 0.85rem;
  color: var(--color-text);
  opacity: 0.7;
  margin-top: 4px;
}

.win-ratio-group {
  grid-column: span 2;
}

.ratio-slider {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ratio-slider input[type="range"] {
  width: 100%;
  height: 8px;
  background: var(--color-background-mute);
  border-radius: 5px;
  outline: none;
  border: none;
  padding: 0;
}

.ratio-slider input[type="range"]::-webkit-slider-thumb {
  appearance: none;
  width: 20px;
  height: 20px;
  background: var(--vt-c-accent);
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(var(--vt-c-accent-rgb), 0.4);
}

.ratio-slider input[type="range"]::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: var(--vt-c-accent);
  border-radius: 50%;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 8px rgba(var(--vt-c-accent-rgb), 0.4);
}

.ratio-display {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ratio-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--vt-c-accent);
}

.ratio-description {
  font-size: 0.9rem;
  color: var(--color-text);
  opacity: 0.8;
  font-weight: 500;
}

.config-summary {
  background-color: var(--color-background-tertiary);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: clamp(20px, 3vw, 36px);
}

.config-summary h4 {
  font-size: clamp(1.1rem, 1.5vw, 1.4rem);
  font-weight: 600;
  color: var(--color-heading);
  margin-bottom: clamp(16px, 2.5vw, 28px);
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: clamp(12px, 1.8vw, 20px) clamp(16px, 2vw, 24px);
  background-color: var(--color-background-mute);
  border-radius: 8px;
}

.stat-label {
  font-weight: 500;
  color: var(--color-text);
  opacity: 0.8;
}

.stat-value {
  font-weight: 600;
  color: var(--vt-c-accent);
}

/* Message Section */
.message-content {
  width: 100%;
  max-width: var(--base-layout-schedule-form-max-width);
  margin-inline: auto;
}

.message-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(1.1rem, 2.3vw, 3rem);
}

.message-input-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.message-input-group label {
  font-weight: 600;
  color: var(--color-heading);
  font-size: clamp(0.95rem, 1.2vw, 1.15rem);
}

.message-input-group textarea {
  padding: clamp(12px, 1.6vw, 20px);
  border: 2px solid var(--color-border);
  border-radius: 12px;
  background-color: var(--color-background-soft);
  color: var(--color-text);
  font-size: clamp(0.95rem, 1.2vw, 1.15rem);
  font-family: inherit;
  resize: vertical;
  min-height: clamp(110px, 20vh, 200px);
  transition: all 0.3s ease;
}

.message-input-group textarea:focus {
  outline: none;
  border-color: var(--vt-c-accent);
  box-shadow: 0 0 0 3px rgba(var(--vt-c-accent-rgb), 0.2);
  background-color: var(--color-background-secondary);
}

.message-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: clamp(8px, 1.6vw, 16px);
}

.character-count {
  font-size: clamp(0.9rem, 1.1vw, 1rem);
  color: var(--color-text);
  opacity: 0.7;
  font-weight: 500;
}

.character-count.near-limit {
  color: #fbbf24;
  opacity: 1;
}

.character-count.at-limit {
  color: #ef4444;
  opacity: 1;
  font-weight: 600;
}

.message-tip {
  font-size: clamp(0.85rem, 1.1vw, 1rem);
  color: var(--color-text);
  opacity: 0.6;
  font-style: italic;
}

.message-preview h4 {
  font-size: clamp(1.05rem, 1.4vw, 1.3rem);
  font-weight: 600;
  color: var(--color-heading);
  margin-bottom: 20px;
}

.prize-modal-preview {
  background-color: var(--color-background-soft);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: clamp(18px, 2.2vw, 32px);
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  aspect-ratio: 2 / 1;
  width: clamp(280px, 46%, 600px);
  margin: 0 auto;
  min-height: clamp(260px, 26vh, 520px);
}

.modal-header {
  font-size: clamp(1.2rem, 1.7vw, 1.5rem);
  font-weight: 700;
  color: var(--vt-c-accent);
  margin-bottom: 15px;
}

.modal-message {
  color: var(--color-text);
  line-height: 1.6;
  margin-bottom: clamp(16px, 2.5vw, 28px);
  min-height: clamp(50px, 8vh, 110px);
  opacity: 0.9;
}

.modal-button {
  background: var(--primary-gradient);
  color: var(--vt-c-background-primary);
  border: none;
  padding: clamp(12px, 1.8vw, 18px) clamp(24px, 2.8vw, 32px);
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: auto;
  height: auto;
  margin: 0 auto;
  display: block;
  font-size: clamp(1rem, 1.3vw, 1.2rem);
}

.modal-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(var(--vt-c-accent-rgb), 0.3);
}

.template-content h3 {
  font-size: clamp(1.7rem, 2.2vw, 2.6rem);
  font-weight: 700;
  color: var(--color-heading);
  margin-bottom: 8px;
  text-align: center;
}

.template-content p {
  color: var(--color-text);
  opacity: 0.9;
  text-align: center;
  margin-bottom: clamp(32px, 4.5vw, 56px);
}

.template-selector-wrapper {
  width: 100%;
  max-width: var(--base-layout-selection-grid-max-width);
  margin: clamp(2.2rem, 4.2vw, 3.5rem) auto 0;
}

/* Config */
.config-section {
  width: 100%;
}

@media (max-width: 1024px) {
  .steps-wrapper {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .template-selector-wrapper {
    margin-top: 2rem;
  }

  .feature-grid, .experience-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .service-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .win-ratio-group {
    grid-column: span 1;
  }

  .drops-form {
    padding: 25px;
  }

  .message-form {
    grid-template-columns: 1fr;
    gap: 25px;
  }

  .feature-grid, .experience-grid, .service-grid {
    grid-template-columns: 1fr;
  }
}

/* Section Progress Indicator - Styles moved to base.css for responsive scaling */

/* Scroll Instructions */
.scroll-instructions {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 998;
  animation: fadeInBounce 2s ease-out;
}

.instruction-text {
  background-color: var(--color-background);
  backdrop-filter: blur(10px);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  padding: 12px 20px;
  font-size: 0.9rem;
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  animation: pulse 2s infinite;
}

.scroll-icon {
  font-size: 1.2rem;
  animation: bounce 2s infinite;
}

@keyframes fadeInBounce {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-4px);
  }
  60% {
    transform: translateY(-2px);
  }
}

@media (max-width: 1024px) {
  .section-progress {
    display: none;
  }

  .scroll-instructions {
    display: none;
  }
}

.progress-indicator {
  margin-top: 16px;
  padding: 16px;
  background-color: var(--color-background-mute);
  border-radius: 8px;
  border: 1px solid var(--color-border);
}

.progress-bar {
  width: 100%;
  height: 8px;
  background-color: var(--color-background-soft);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--vt-c-accent), var(--vt-c-green));
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.85rem;
  color: var(--color-text);
  font-weight: 500;
}

/* Asset Selector Styles */
.asset-selector-section {
  margin: 20px 0;
  padding: 20px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  border: 1px solid rgba(150, 254, 201, 0.2);
}

.asset-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.asset-category h5 {
  color: #292e50;
  margin: 0 0 8px 0;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.asset-option-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}

.asset-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  background: rgba(255, 255, 255, 0.8);
  border: 2px solid rgba(150, 254, 201, 0.3);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.asset-option.selected {
  border-color: var(--vt-c-accent);
  background: rgba(150, 254, 201, 0.1);
  box-shadow: 0 0 12px rgba(150, 254, 201, 0.3);
}

.asset-option:hover {
  border-color: var(--vt-c-accent);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(150, 254, 201, 0.2);
}

.asset-preview {
  font-size: 1.5rem;
  margin-bottom: 8px;
}

.asset-option span {
  font-size: 0.8rem;
  font-weight: 600;
  color: #292e50;
  text-align: center;
}

@media (max-width: 768px) {
  .asset-option-grid {
    grid-template-columns: 1fr;
  }

  .asset-selector-section {
    padding: 16px;
  }
}

#company-details {
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.form-row-center {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
}

.toggle-option {
  padding: 10px 20px;
  border: 2px solid var(--vt-c-accent);
  border-radius: 15px;
  margin: 0 10px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.toggle-option.active {
  background-color: var(--vt-c-accent);
  color: var(--color-background);
}

.toggle-slider {
  width: 40px;
  height: 20px;
  background-color: var(--color-background-mute);
  border-radius: 10px;
  position: relative;
  transition: transform 0.3s;
}

.toggle-slider::before {
  content: '';
  width: 16px;
  height: 16px;
  background-color: var(--vt-c-accent);
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: transform 0.3s;
}

.toggle-option.active + .toggle-slider::before {
  transform: translateX(20px);
}

.toggle-option:not(.active) + .toggle-slider {
  transform: translateX(0);
}

.details-form h4 {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--color-heading);
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-border);
}

.company-fields {
  margin-top: 24px;
}

.details-form {
  background-color: var(--color-background-soft);
  border-radius: 16px;
  padding: clamp(1.5rem, 4vh, 2.5rem);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(var(--vt-c-accent-rgb), 0.1);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(1rem, 2.4vw, 1.8rem);
  margin-bottom: clamp(12px, 2vw, 20px);
}

/* Global Navigation */
.global-navigation {
  position: fixed;
  bottom: var(--control-nav-offset-bottom);
  left: 0;
  width: 100%;
  padding: 0 1rem;
  background-color: transparent;
  border-top: none;
  z-index: 1500;
  box-sizing: border-box;
  pointer-events: none; /* Allow click through to content behind bar */
  --footer-nav-button-size: var(--control-nav-size);
}

.global-navigation__container {
  pointer-events: auto; /* Re-enable for buttons */
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--control-nav-gap);
}

.global-navigation__actions {
  display: flex;
  align-items: center;
  gap: var(--control-nav-gap);
}

.global-navigation .help-button {
  height: var(--footer-nav-button-size);
  width: var(--footer-nav-button-size);
  border-radius: 999px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid var(--vt-c-accent);
  cursor: pointer;
  background: var(--vt-c-accent);
  color: var(--vt-c-background-primary);
  font-size: var(--control-nav-icon-size);
  line-height: 1;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.global-navigation .help-button .button-text {
  font-size: var(--control-nav-text-size);
  letter-spacing: 0.065em;
  text-transform: uppercase;
}

.global-navigation .help-button:hover:not(:disabled) {
  background: var(--vt-c-accent);
  color: var(--vt-c-background-primary);
}

.global-navigation .help-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.global-navigation .back-button {
  height: var(--footer-nav-button-size);
  width: var(--footer-nav-button-size);
  border-radius: 999px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid var(--vt-c-accent);
  cursor: pointer;
  background: var(--vt-c-accent);
  color: var(--vt-c-background-primary);
  font-size: var(--control-nav-text-size);
  line-height: 1;
  letter-spacing: 0.065em;
  text-transform: uppercase;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.global-navigation .back-button:not(:disabled):hover {
  background: var(--vt-c-accent);
  color: var(--vt-c-background-primary);
}

.global-navigation .back-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.global-navigation .continue-button {
  height: var(--footer-nav-button-size);
  width: var(--footer-nav-button-size);
  border-radius: 999px;
  background: var(--vt-c-accent);
  color: var(--vt-c-background-primary);
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 500ms cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid var(--vt-c-accent);
  cursor: pointer;
  box-shadow: 0 10px 15px -3px rgba(var(--vt-c-accent-rgb), 0.1);
  font-size: var(--control-nav-text-size);
  line-height: 1;
  letter-spacing: 0.065em;
  text-transform: uppercase;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.global-navigation .continue-button.continue-button--final {
  width: auto;
  min-width: var(--control-nav-final-min-width);
  padding: 0 var(--control-nav-final-padding-inline);
}

.global-navigation .continue-button .button-text--final {
  font-size: var(--control-nav-final-text-size);
  line-height: 1;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.global-navigation .continue-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
  box-shadow: none;
}

.global-navigation .continue-button:not(:disabled):hover {
  background: var(--vt-c-accent);
  color: var(--vt-c-background-primary);
}

.global-navigation .continue-button:not(:disabled):active {
  background: var(--vt-c-accent);
  color: var(--vt-c-background-primary);
  transition: all 50ms ease-out;
  transform: scale(0.98);
}

.experience-attach-section {
  display: flex;
  justify-content: center;
  align-items: center !important;
}

.experience-attach-shell {
  width: min(100%, var(--base-layout-section-max-width));
  max-width: 42rem;
  margin-inline: auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1rem;
}

.experience-attach-section .section-header {
  text-align: center;
  width: 100%;
}

.experience-attach-options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  width: 100%;
}

.experience-attach-option {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: 0.85rem 0.75rem;
  border-radius: var(--base-layout-button-border-radius-lg, 1rem);
  border: 1px solid var(--vt-c-accent);
  background: var(--vt-c-accent);
  color: #0a1628;
  text-align: center;
  cursor: pointer;
  transition: background 150ms ease;
  box-shadow: none;
  aspect-ratio: auto;
}

.experience-attach-option:hover {
  background: var(--ui-inverse-cta-bg-hover);
}

.experience-attach-option--active {
  background: #50e5ff;
  border-color: #50e5ff;
}

.experience-attach-option__title {
  color: #0a1628;
  font-weight: 700;
  font-size: 0.95rem;
  line-height: 1.25;
}

.experience-attach-library {
  display: grid;
  gap: 0.75rem;
  padding: 1rem 1.1rem;
  border-radius: 1rem;
  border: 1px solid var(--color-border);
  background: var(--color-background-soft);
  width: 100%;
}

.experience-attach-empty {
  color: var(--color-text);
  opacity: 0.75;
}

.experience-attach-select {
  display: grid;
  gap: 0.25rem;
  color: var(--color-heading);
  font-weight: 600;
}

.experience-attach-select__head {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: baseline;
}

.experience-attach-select__hint {
  font-size: 0.78rem;
  color: var(--vt-c-accent);
  opacity: 0.9;
  font-weight: 700;
}

.experience-attach-list {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding-bottom: 0.1rem;
}

.experience-attach-group {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.experience-attach-group__title {
  position: sticky;
  top: 0;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: var(--color-heading);
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.experience-attach-record {
  display: grid;
  gap: 0.48rem;
  min-height: 5.35rem;
  padding: 0.98rem 1.02rem 1.02rem;
  border-radius: 0.9rem;
  border: 1px solid var(--color-border);
  background: var(--color-background-mute);
  color: var(--color-text);
  text-align: left;
  cursor: pointer;
}

.experience-attach-pagination {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.1rem;
}

.experience-attach-pagination__button {
  min-width: 6.5rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.26);
  background: rgba(255, 255, 255, 0.04);
  color: var(--vt-c-accent);
  border-radius: 999px;
  padding: 0.55rem 0.9rem;
  font-weight: 700;
  cursor: pointer;
}

.experience-attach-pagination__button:disabled {
  opacity: 0.45;
  cursor: default;
}

.experience-attach-record--active {
  border-color: var(--vt-c-accent);
  box-shadow: 0 0 0 1px rgba(var(--vt-c-accent-rgb), 0.18);
}

.experience-attach-record__top {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: flex-start;
}

.experience-attach-record__identity {
  display: grid;
  gap: 0.12rem;
}

.experience-attach-record__name {
  color: var(--color-heading);
  font-weight: 700;
}

.experience-attach-record__key {
  color: var(--color-text);
  opacity: 0.6;
  font-size: 0.76rem;
  word-break: break-word;
}

.experience-attach-record__status {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
}

.experience-attach-record__status--draft {
  background: rgba(255, 209, 102, 0.14);
  color: #ffd166;
}

.experience-attach-record__status--published {
  background: rgba(57, 255, 20, 0.14);
  color: #8dff78;
}

.experience-attach-record__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  color: var(--color-text);
  opacity: 0.78;
  font-size: 0.84rem;
}

.experience-attach-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  color: var(--color-text);
  opacity: 0.82;
  font-size: 0.9rem;
}

.experience-attach-current {
  display: grid;
  gap: 0.35rem;
  padding: 0.72rem 0.95rem;
  border-radius: 0.9rem;
  border: 1px solid rgba(var(--vt-c-accent-rgb), 0.22);
  background: linear-gradient(180deg, rgba(var(--vt-c-accent-rgb), 0.08), rgba(255, 255, 255, 0.03));
}

.experience-attach-current__eyebrow {
  color: rgba(var(--vt-c-accent-rgb), 0.88);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.experience-attach-current__meta {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.55rem;
}

.experience-attach-current__badges {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.experience-attach-current__identity {
  display: grid;
  gap: 0.04rem;
}

.experience-attach-current__title {
  color: var(--color-heading);
  font-weight: 700;
  line-height: 1.2;
}

.experience-attach-current__key {
  color: var(--color-text);
  opacity: 0.68;
  font-size: 0.74rem;
  word-break: break-word;
}

.experience-attach-current__status {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
}

.experience-attach-current__status--draft {
  background: rgba(255, 209, 102, 0.14);
  color: #ffd166;
}

.experience-attach-current__status--published {
  background: rgba(57, 255, 20, 0.14);
  color: #8dff78;
}

.experience-attach-current__version,
.experience-attach-record__version {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.78);
  background: rgba(255, 255, 255, 0.08);
}

.experience-attach-upgrade {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  flex-wrap: wrap;
  padding: 0.5rem 0.7rem;
  border-radius: 0.75rem;
  background: rgba(var(--vt-c-accent-rgb), 0.06);
  border: 1px dashed rgba(var(--vt-c-accent-rgb), 0.24);
  color: var(--color-text);
  font-size: 0.82rem;
}

.experience-attach-current__actions {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.experience-attach-inline-link,
.experience-attach-inline-button {
  color: var(--vt-c-accent);
  font-weight: 700;
  text-decoration: none;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
}

.experience-attach-record__badges {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}

@media (max-width: 1366px) {
  .global-navigation {
    --footer-nav-button-size: max(3.15rem, calc(var(--control-nav-size) * 0.9));
  }

  .global-navigation .help-button {
    font-size: max(1.45rem, calc(var(--control-nav-icon-size) * 0.92));
  }

  .global-navigation .back-button,
  .global-navigation .continue-button {
    font-size: max(0.72rem, calc(var(--control-nav-text-size) * 0.9));
  }
}

@media (max-width: 768px) {
  .experience-attach-header {
    flex-direction: column;
  }

  .experience-attach-options {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .global-navigation {
    bottom: var(--control-nav-offset-bottom);
  }

  .global-navigation__container,
  .global-navigation__actions {
    gap: var(--control-nav-gap);
  }

  .global-navigation .help-button {
    font-size: var(--control-nav-icon-size);
  }

  .global-navigation .back-button,
  .global-navigation .continue-button {
    font-size: var(--control-nav-text-size);
  }
}
</style>
